import * as React from "react"
import { useTranslation } from "react-i18next"
import {
  Folder, FolderOpen, FileText, Link2, Plus, Trash2, Pencil, Upload,
  ChevronRight, LayoutGrid, List, ArrowLeft, Search,
  Globe, File, Image, FileSpreadsheet, ArrowUp, ArrowDown, ArrowUpDown, Database,
  Tag, Download, RefreshCw, FileUp, X, FolderInput, AlertTriangle, Check,
} from "lucide-react"

import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select"
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Badge } from "@/components/ui/badge"
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"
import { useTags } from "@/hooks/use-tags"
import { ComoLoTrataElAgente } from "./ComoLoTrataElAgente"
import { WhoUsesIt } from "./WhoUsesIt"
import { useIsMobile } from "@/hooks/use-mobile"
import { documentTagService } from "@/services/DocumentTagService"
import { RAG_API_URL, getAuthToken } from "@/config/env"
import { toast } from "@/lib/toast"
import { toast as sonnerToast } from "sonner"

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type SortField = "name" | "type" | "size" | "date" | "status"
type SortDir = "asc" | "desc"

type ItemKind = "folder" | "document" | "url"

interface ContentItem {
  id: string
  kind: ItemKind
  name: string
  parentId: string | null
  // document fields
  description?: string
  category?: string
  subcategory?: string
  fileType?: string
  size?: string
  status?: "uploaded" | "processing" | "embedded" | "failed"
  addedDate?: string
  // url fields
  url?: string
  isPublic?: boolean
  // file path/url returned by backend (or sample docs in local demo mode)
  filePath?: string
  // optional hydrated tags from API-backed items
  tagIds?: string[]
}

interface ContentCategoryOption {
  id: string
  name: string
  subcategories: Array<{ id: string; name: string }>
}

interface ContentDocumentUpdatePayload {
  title: string
  description?: string
  category?: string | null
  subcategory?: string | null
  directory?: string | null
  file?: File
}

interface ContentUrlUpdatePayload {
  title: string
  url: string
  description?: string
  category?: string | null
  subcategory?: string | null
  isPublic?: boolean
}

type FileKind = "pdf" | "txt" | "csv" | "doc"
type FilterKind = "all" | ItemKind

interface PdfJsPage {
  getViewport: (options: { scale: number }) => { width: number; height: number }
  render: (options: { canvasContext: CanvasRenderingContext2D; viewport: { width: number; height: number } }) => { promise: Promise<void> }
}

interface PdfJsDocument {
  getPage: (pageNumber: number) => Promise<PdfJsPage>
}

interface PdfJsLib {
  GlobalWorkerOptions: { workerSrc: string }
  getDocument: (url: string) => { promise: Promise<PdfJsDocument> }
}

/* ------------------------------------------------------------------ */
/*  Available tags (shared with TagsManager mock data)                 */
/* ------------------------------------------------------------------ */

interface AvailableTag {
  id: string
  name: string
  color: string
}

const INITIAL_TAG_ASSIGNMENTS: Record<string, string[]> = {}

/* ------------------------------------------------------------------ */
/*  Seed data                                                          */
/* ------------------------------------------------------------------ */

let _id = 100
function uid() { return `c_${++_id}_${Date.now()}` }

function buildSeed(): ContentItem[] {
  const now = new Date()
  const ago = (d: number) => new Date(now.getTime() - d * 864e5).toISOString()

  return [
    // root folders
    { id: "f1", kind: "folder", name: "Marketing", parentId: null },
    { id: "f2", kind: "folder", name: "Legal", parentId: null },
    { id: "f3", kind: "folder", name: "Product", parentId: null },

    // subfolder
    { id: "f1a", kind: "folder", name: "Campaigns", parentId: "f1" },
    { id: "f1b", kind: "folder", name: "Brand Assets", parentId: "f1" },
    { id: "f3a", kind: "folder", name: "Roadmap", parentId: "f3" },

    // docs at root (with real file paths for preview)
    { id: "d1", kind: "document", name: "Getting Started Guide.pdf", parentId: null, description: "Onboarding guide for new users", category: "General", fileType: "pdf", size: "2.4 MB", status: "embedded", addedDate: ago(2), filePath: "/sample-docs/getting-started-guide.pdf" },
    { id: "d2", kind: "document", name: "FAQ.txt", parentId: null, description: "Frequently asked questions", category: "Support", fileType: "txt", size: "48 KB", status: "embedded", addedDate: ago(5), filePath: "/sample-docs/faq.txt" },
    { id: "d9", kind: "document", name: "Industry List.csv", parentId: null, description: "Industry categories reference", category: "Data", fileType: "csv", size: "1.2 KB", status: "embedded", addedDate: ago(3), filePath: "/sample-docs/roadmap-2026.csv" },

    // urls at root
    { id: "u1", kind: "url", name: "Company Blog", parentId: null, description: "Main company blog with articles", url: "https://blog.example.com", status: "embedded", isPublic: true, addedDate: ago(1) },
    { id: "u2", kind: "url", name: "API Documentation", parentId: null, description: "Developer API reference", url: "https://docs.example.com/api", status: "embedded", isPublic: false, addedDate: ago(7) },

    // docs inside folders (with real file paths)
    { id: "d3", kind: "document", name: "Campaign Brief Q1.pdf", parentId: "f1a", description: "Q1 campaign strategy", category: "Marketing", fileType: "pdf", size: "1.1 MB", status: "embedded", addedDate: ago(12), filePath: "/sample-docs/campaign-brief.pdf" },
    { id: "d4", kind: "document", name: "Brand Guidelines.pdf", parentId: "f1b", description: "Official brand identity guide", category: "Marketing", fileType: "pdf", size: "8.2 MB", status: "uploaded", addedDate: ago(3), filePath: "/sample-docs/brand-guidelines.pdf" },
    { id: "d5", kind: "document", name: "Terms of Service.pdf", parentId: "f2", description: "Legal terms and conditions", category: "Legal", fileType: "pdf", size: "340 KB", status: "embedded", addedDate: ago(30), filePath: "/sample-docs/terms-of-service.pdf" },
    { id: "d6", kind: "document", name: "Privacy Policy.pdf", parentId: "f2", description: "GDPR compliant privacy policy", category: "Legal", fileType: "pdf", size: "280 KB", status: "embedded", addedDate: ago(30), filePath: "/sample-docs/privacy-policy.pdf" },
    { id: "u3", kind: "url", name: "Competitor Analysis", parentId: "f1", description: "Spreadsheet with competitor research", url: "https://sheets.google.com/example", status: "processing", isPublic: false, addedDate: ago(0) },
    { id: "d7", kind: "document", name: "Roadmap 2026.csv", parentId: "f3a", description: "Product roadmap data", category: "Product", fileType: "csv", size: "96 KB", status: "embedded", addedDate: ago(8), filePath: "/sample-docs/roadmap-2026.csv" },
    { id: "d8", kind: "document", name: "Logo Pack.png", parentId: "f1b", description: "Logo variations", category: "Marketing", fileType: "png", size: "4.5 MB", status: "uploaded", addedDate: ago(4) },
  ]
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

/**
 * El tinte de una imagen. Es el unico color de este fichero que no es un
 * estado, y por eso no tiene token: una imagen no «va bien» ni «falla». El
 * estandar lo admite como excepcion -reconocer un tipo de archivo de un
 * vistazo- a condicion de que viva en un solo sitio, que es este.
 */
// eslint-disable-next-line no-restricted-syntax -- tinte por tipo de archivo, excepcion 2 del estandar de diseno
const IMAGEN = { icono: "text-purple-500", baldosa: "bg-purple-500/15", fondo: "bg-purple-500/5", caja: "bg-purple-500/15 ring-1 ring-purple-500/20", linea: "bg-purple-500/10" }

function getFileIcon(item: ContentItem, size: "sm" | "md" = "md") {
  const cls = size === "sm" ? "h-3.5 w-3.5" : "h-5 w-5"
  if (item.kind === "url") return <Globe className={cn(cls, "text-primary/80")} />
  const ft = item.fileType?.toLowerCase() || ""
  if (ft === "pdf") return <FileText className={cn(cls, "text-destructive")} />
  if (ft === "csv") return <FileSpreadsheet className={cn(cls, "text-success")} />
  if (["png", "jpg", "jpeg", "gif", "webp", "svg"].includes(ft)) return <Image className={cn(cls, IMAGEN.icono)} />
  return <File className={cn(cls, "text-muted-foreground")} />
}

/** Color palette for folder child preview tiles */
const tileBg: Record<string, string> = {
  url: "bg-primary/15",
  pdf: "bg-destructive/15",
  csv: "bg-success/15",
  png: IMAGEN.baldosa, jpg: IMAGEN.baldosa, jpeg: IMAGEN.baldosa, gif: IMAGEN.baldosa, webp: IMAGEN.baldosa, svg: IMAGEN.baldosa,
  txt: "bg-muted/15",
  folder: "bg-warning/15",
}
function getTileBg(item: ContentItem) {
  if (item.kind === "folder") return tileBg.folder
  if (item.kind === "url") return tileBg.url
  return tileBg[item.fileType?.toLowerCase() || ""] || "bg-muted/60"
}

function statusColor(s?: string) {
  if (s === "embedded") return "bg-success/15 text-success border-success/20"
  if (s === "processing") return "bg-warning/15 text-warning border-warning/20"
  if (s === "failed") return "bg-destructive/15 text-destructive border-destructive/20"
  return "bg-muted text-muted-foreground"
}

function isAbsoluteHttpUrl(value: string) {
  return value.startsWith("http://") || value.startsWith("https://")
}

function getBackendDocumentId(item: ContentItem): string | null {
  if (item.kind !== "document") return null
  const match = /^doc-(\d+)$/.exec(item.id)
  return match ? match[1] : null
}

function resolveDownloadUrl(item: ContentItem): string | null {
  const backendDocumentId = getBackendDocumentId(item)
  if (backendDocumentId) {
    return `/api/v1/rag/documents/${backendDocumentId}/download/`
  }

  const rawPath = item.filePath?.trim()
  if (!rawPath) return null

  if (isAbsoluteHttpUrl(rawPath)) return rawPath
  if (rawPath.startsWith("file://")) return null

  const base = RAG_API_URL && RAG_API_URL.trim()
    ? RAG_API_URL.replace(/\/$/, "")
    : (typeof window !== "undefined" ? window.location.origin : "")

  let normalizedPath = rawPath
  if (!normalizedPath.startsWith("/")) {
    normalizedPath = normalizedPath.startsWith("media/")
      ? `/${normalizedPath}`
      : `/media/${normalizedPath}`
  }

  return base ? `${base}${normalizedPath}` : normalizedPath
}

function getFilenameFromContentDisposition(contentDisposition: string | null): string | null {
  if (!contentDisposition) return null

  // RFC 5987/6266 form: filename*=UTF-8''encoded-name.ext
  const utf8Match = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(contentDisposition)
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].trim().replace(/^"|"$/g, ""))
    } catch {
      return utf8Match[1].trim().replace(/^"|"$/g, "")
    }
  }

  // Basic form: filename="name.ext" or filename=name.ext
  const basicMatch = /filename\s*=\s*("?)([^";]+)\1/i.exec(contentDisposition)
  if (basicMatch?.[2]) {
    return basicMatch[2].trim()
  }

  return null
}

/* ------------------------------------------------------------------ */
/*  Thumbnail components                                               */
/* ------------------------------------------------------------------ */

// Cache rendered PDF page data-urls globally so we don't re-render on every mount
const pdfPageCache = new Map<string, string>()

/** Load pdfjs from CDN once */
let pdfjsPromise: Promise<PdfJsLib> | null = null
function loadPdfjs() {
  if (pdfjsPromise) return pdfjsPromise
  const windowWithPdf = window as Window & { pdfjsLib?: PdfJsLib }
  pdfjsPromise = new Promise<PdfJsLib>((resolve, reject) => {
    if (windowWithPdf.pdfjsLib) { resolve(windowWithPdf.pdfjsLib); return }
    const s = document.createElement("script")
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.2.67/pdf.min.mjs"
    s.type = "module"
    // For module scripts we need a different approach — use importmap/dynamic import
    // Actually simpler: use the UMD build
    s.remove()
    const s2 = document.createElement("script")
    s2.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js"
    s2.onload = () => {
      const lib = windowWithPdf.pdfjsLib
      if (lib) {
        lib.GlobalWorkerOptions.workerSrc = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js"
        resolve(lib)
      } else reject(new Error("pdfjsLib not found"))
    }
    s2.onerror = reject
    document.head.appendChild(s2)
  })
  return pdfjsPromise
}

/** Render first page of a PDF to a data-URL (cached) */
async function renderPdfPage(url: string, width = 400): Promise<string | null> {
  if (pdfPageCache.has(url)) return pdfPageCache.get(url)!
  try {
    const pdfjsLib = await loadPdfjs()
    const pdf = await pdfjsLib.getDocument(url).promise
    const page = await pdf.getPage(1)
    const viewport = page.getViewport({ scale: width / page.getViewport({ scale: 1 }).width })
    const canvas = document.createElement("canvas")
    canvas.width = viewport.width
    canvas.height = viewport.height
    await page.render({ canvasContext: canvas.getContext("2d")!, viewport }).promise
    const dataUrl = canvas.toDataURL("image/jpeg", 0.8)
    pdfPageCache.set(url, dataUrl)
    return dataUrl
  } catch {
    return null
  }
}

/** PDF thumbnail: renders real first page via pdfjs with file-type icon overlay */
function PdfThumbnail({ item, className }: { item: ContentItem; className?: string }) {
  const previewUrl = React.useMemo(() => resolveDownloadUrl(item), [item])
  const [src, setSrc] = React.useState<string | null>(previewUrl ? (pdfPageCache.get(previewUrl) || null) : null)
  const [failed, setFailed] = React.useState(false)

  React.useEffect(() => {
    if (!previewUrl || src) return
    let cancelled = false
    renderPdfPage(previewUrl).then((result) => {
      if (cancelled) return
      if (result) setSrc(result)
      else setFailed(true)
    })
    return () => { cancelled = true }
  }, [previewUrl, src])

  return (
    <div className={cn("relative overflow-hidden bg-destructive/5", className)}>
      {src ? (
        <img src={src} alt={item.name} className="absolute inset-0 w-full h-full object-cover object-top" />
      ) : failed ? (
        /* fallback: simulated lines */
        <FallbackDocLines color="red" />
      ) : (
        /* loading shimmer */
        <div className="absolute inset-0 animate-pulse bg-muted/10" />
      )}
      {/* file type badge overlay */}
      <div className="absolute bottom-1.5 right-1.5">
        <div className="flex items-center justify-center w-7 h-7 rounded-md bg-destructive/20 ring-1 ring-destructive/30 shadow-sm">
          <FileText className="h-3.5 w-3.5 text-destructive" />
        </div>
      </div>
    </div>
  )
}

// TXT file content cache
const txtCache = new Map<string, string>()

/** TXT thumbnail: renders real text content */
function TxtThumbnail({ item, className }: { item: ContentItem; className?: string }) {
  const previewUrl = React.useMemo(() => resolveDownloadUrl(item), [item])
  const [text, setText] = React.useState<string | null>(previewUrl ? (txtCache.get(previewUrl) || null) : null)
  const [failed, setFailed] = React.useState(false)

  React.useEffect(() => {
    if (!previewUrl || text !== null) return
    let cancelled = false
    fetch(previewUrl)
      .then((r) => { if (!r.ok) throw new Error(); return r.text() })
      .then((t) => { if (!cancelled) { txtCache.set(previewUrl, t); setText(t) } })
      .catch(() => { if (!cancelled) setFailed(true) })
    return () => { cancelled = true }
  }, [previewUrl, text])

  return (
    <div className={cn("relative overflow-hidden bg-muted/5", className)}>
      {text !== null ? (
        <div className="absolute inset-0 p-2.5 overflow-hidden">
          <p className="text-[7px] leading-[10px] text-muted-foreground font-mono whitespace-pre-wrap break-all line-clamp-[14]">
            {text.slice(0, 800)}
          </p>
        </div>
      ) : failed ? (
        <FallbackDocLines color="slate" />
      ) : (
        <div className="absolute inset-0 animate-pulse bg-muted/10" />
      )}
      {/* file type badge */}
      <div className="absolute bottom-1.5 right-1.5">
        <div className="flex items-center justify-center w-7 h-7 rounded-md bg-muted/15 ring-1 ring-border shadow-sm">
          <File className="h-3.5 w-3.5 text-muted-foreground" />
        </div>
      </div>
    </div>
  )
}

// CSV file content cache
const csvCache = new Map<string, string[][]>()

/** CSV thumbnail: renders a mini table preview */
function CsvThumbnail({ item, className }: { item: ContentItem; className?: string }) {
  const previewUrl = React.useMemo(() => resolveDownloadUrl(item), [item])
  const [rows, setRows] = React.useState<string[][] | null>(previewUrl ? (csvCache.get(previewUrl) || null) : null)
  const [failed, setFailed] = React.useState(false)

  React.useEffect(() => {
    if (!previewUrl || rows !== null) return
    let cancelled = false
    fetch(previewUrl)
      .then((r) => { if (!r.ok) throw new Error(); return r.text() })
      .then((text) => {
        if (cancelled) return
        const parsed = text.trim().split("\n").slice(0, 8).map(line =>
          line.split(/[,;\t]/).map(cell => cell.replace(/^"|"$/g, "").trim())
        )
        csvCache.set(previewUrl, parsed)
        setRows(parsed)
      })
      .catch(() => { if (!cancelled) setFailed(true) })
    return () => { cancelled = true }
  }, [previewUrl, rows])

  return (
    <div className={cn("relative overflow-hidden bg-success/5", className)}>
      {rows ? (
        <div className="absolute inset-0 p-1.5 overflow-hidden">
          <table className="w-full border-collapse">
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri}>
                  {row.slice(0, 4).map((cell, ci) => (
                    <td
                      key={ci}
                      className={cn(
                        "px-1 py-px text-[6px] leading-[9px] font-mono truncate max-w-[60px] border-b border-r border-success/10",
                        ri === 0 ? "text-success/70 font-semibold bg-success/10" : "text-muted-foreground"
                      )}
                    >
                      {cell.slice(0, 16)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : failed ? (
        <FallbackDocLines color="green" />
      ) : (
        <div className="absolute inset-0 animate-pulse bg-success/5" />
      )}
      {/* file type badge */}
      <div className="absolute bottom-1.5 right-1.5">
        <div className="flex items-center justify-center w-7 h-7 rounded-md bg-success/20 ring-1 ring-success/30 shadow-sm">
          <FileSpreadsheet className="h-3.5 w-3.5 text-success" />
        </div>
      </div>
    </div>
  )
}

/** Generic document thumbnail for types without real preview (PNG, etc.) */
function GenericDocThumbnail({ item, className }: { item: ContentItem; className?: string }) {
  const ft = item.fileType?.toLowerCase() || ""
  const isCsv = ft === "csv"
  const isImg = ["png", "jpg", "jpeg", "gif", "webp", "svg"].includes(ft)
  const color = isCsv ? "green" : isImg ? "purple" : "slate"
  const bgClass = isCsv ? "bg-success/5" : isImg ? IMAGEN.fondo : "bg-muted/5"

  return (
    <div className={cn("relative overflow-hidden", bgClass, className)}>
      <FallbackDocLines color={color} />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className={cn(
          "flex items-center justify-center w-10 h-10 rounded-lg shadow-sm",
          color === "green" ? "bg-success/15 ring-1 ring-success/20" :
          color === "purple" ? IMAGEN.caja :
          "bg-muted/40 ring-1 ring-border"
        )}>
          {getFileIcon(item)}
        </div>
      </div>
    </div>
  )
}

/** Simulated text lines as fallback for docs without real preview */
function FallbackDocLines({ color }: { color: string }) {
  const lineColor =
    color === "red" ? "bg-destructive/10" :
    color === "green" ? "bg-success/10" :
    color === "purple" ? IMAGEN.linea :
    "bg-muted-foreground"

  return (
    <div className="absolute inset-0 p-3 pt-2.5 space-y-1 opacity-60">
      <div className={cn("h-[5px] rounded-full w-3/5", lineColor)} style={{ opacity: 0.8 }} />
      <div className="h-[3px]" />
      {Array.from({ length: 7 }).map((_, i) => (
        <div key={i} className={cn("h-[3px] rounded-full", lineColor)} style={{ width: `${60 + Math.sin(i * 1.8) * 25}%`, opacity: 0.5 - i * 0.04 }} />
      ))}
      <div className="h-[4px]" />
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={`b${i}`} className={cn("h-[3px] rounded-full", lineColor)} style={{ width: `${55 + Math.cos(i * 2) * 20}%`, opacity: 0.35 - i * 0.04 }} />
      ))}
    </div>
  )
}

/** Smart thumbnail dispatcher: picks the right preview per file type */
function DocThumbnail({ item, className }: { item: ContentItem; className?: string }) {
  const ft = item.fileType?.toLowerCase() || ""
  if (ft === "pdf" && item.filePath) return <PdfThumbnail item={item} className={className} />
  if (ft === "txt" && item.filePath) return <TxtThumbnail item={item} className={className} />
  if (ft === "csv" && item.filePath) return <CsvThumbnail item={item} className={className} />
  return <GenericDocThumbnail item={item} className={className} />
}

/* ------------------------------------------------------------------ */
/*  Stats badge with hover popover                                     */
/* ------------------------------------------------------------------ */

interface ContentStats {
  totalItems: number
  totalUrls: number
  totalDocuments: number
  lastUpdated: Date
}

function StatsBadge({ stats }: { stats: ContentStats }) {
  const { t } = useTranslation()
  const [hovered, setHovered] = React.useState(false)
  const hoverTimeout = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  const show = () => {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current)
    setHovered(true)
  }
  const hide = () => {
    hoverTimeout.current = setTimeout(() => setHovered(false), 200)
  }

  return (
    <div className="relative" onMouseEnter={show} onMouseLeave={hide}>
      <Button
        variant="link"
        size="xs"
        className="text-muted-foreground hover:text-foreground"
      >
        <Database className="h-3 w-3" />
        <span>{t("knowledge.content.overview")}</span>
      </Button>

      {/* Hover card */}
      {hovered && (
        <div className="absolute left-0 top-full z-50 mt-2 w-64 rounded-lg border bg-popover p-4 shadow-md animate-in fade-in-0 zoom-in-95" onMouseEnter={show} onMouseLeave={hide}>
          <div className="space-y-2">
            <p className="text-xs font-semibold text-muted-foreground">{t("knowledge.content.overview")}</p>
            <div className="grid grid-cols-2 gap-y-2 text-xs">
              <span className="text-muted-foreground">{t("knowledge.total_items")}</span>
              <span className="text-right font-semibold">{stats.totalItems}</span>
              <span className="text-muted-foreground">{t("knowledge.documents")}</span>
              <span className="text-right font-semibold">{stats.totalDocuments}</span>
              <span className="text-muted-foreground">{t("knowledge.urls")}</span>
              <span className="text-right font-semibold">{stats.totalUrls}</span>
            </div>
            <div className="border-t pt-2 text-[10px] text-muted-foreground">
              {t("knowledge.content.lastUpdatedLabel")}{" "}
              <span className="font-medium">{new Date(stats.lastUpdated).toLocaleString()}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

/** El identificador del documento detras del de la pantalla.
 *
 * `ContentManager` numera sus filas `doc-12` / `url-3` / `dir-4` porque mezcla
 * tres cosas en una sola lista, y el Workspace habla de documentos y solo de
 * documentos. Devuelve `null` para todo lo que no sea un documento en vez de
 * un numero cualquiera: un `NaN` acabaria pidiendole al servidor el elemento
 * «NaN».
 */
function idDeDocumento(idDeLaPantalla: string): number | null {
  const crudo = idDeLaPantalla.startsWith("doc-") ? idDeLaPantalla.slice(4) : ""
  return /^\d+$/.test(crudo) ? Number(crudo) : null
}

interface ContentManagerProps {
  stats?: ContentStats
  items?: ContentItem[]
  categories?: ContentCategoryOption[]
  loading?: boolean
  onRequestDocumentUpload?: () => void
  onRequestURLUpload?: () => void
  onCreateFolder?: (payload: { name: string; parentId: string | null }) => Promise<void>
  onUpdateFolder?: (folderId: string, payload: { name?: string; parentId?: string | null }) => Promise<void>
  onUpdateDocument?: (contentId: string, data: ContentDocumentUpdatePayload) => Promise<void>
  onUpdateURL?: (contentId: string, data: ContentUrlUpdatePayload) => Promise<void>
  onDeleteItems?: (contentIds: string[]) => Promise<void>
  onMoveItems?: (moves: Array<{ contentId: string; targetFolderId: string | null }>) => Promise<void>
}

export function ContentManager({
  stats,
  items: externalItems,
  categories = [],
  loading = false,
  onRequestDocumentUpload,
  onRequestURLUpload,
  onCreateFolder,
  onUpdateFolder,
  onUpdateDocument,
  onUpdateURL,
  onDeleteItems,
  onMoveItems,
}: ContentManagerProps) {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const { tags: fetchedTags, loading: tagsLoading, createTag, refresh: refreshTags } = useTags({ autoLoad: true, orderBy: "name", order: "asc" })

  const [items, setItems] = React.useState<ContentItem[]>(() => externalItems ?? buildSeed())
  const [view, setView] = React.useState<"grid" | "list">("grid")
  const [currentFolder, setCurrentFolder] = React.useState<string | null>(null)
  const [search, setSearch] = React.useState("")
  const [mobileSearchOpen, setMobileSearchOpen] = React.useState(false)

  // dialogs
  const [folderDialogOpen, setFolderDialogOpen] = React.useState(false)
  const [editingFolder, setEditingFolder] = React.useState<ContentItem | null>(null)
  const [folderName, setFolderName] = React.useState("")

  const [docDialogOpen, setDocDialogOpen] = React.useState(false)
  const [urlDialogOpen, setUrlDialogOpen] = React.useState(false)

  // doc form
  const [editingDoc, setEditingDoc] = React.useState<ContentItem | null>(null)
  const [docName, setDocName] = React.useState("")
  const [docDesc, setDocDesc] = React.useState("")
  const [docCategory, setDocCategory] = React.useState("")
  const [docSubcategory, setDocSubcategory] = React.useState("")
  const [docType, setDocType] = React.useState<FileKind>("pdf")
  const [docReplaceFile, setDocReplaceFile] = React.useState<File | null>(null)

  // url form
  const [editingUrl, setEditingUrl] = React.useState<ContentItem | null>(null)
  const [urlTitle, setUrlTitle] = React.useState("")
  const [urlUrl, setUrlUrl] = React.useState("")
  const [urlDesc, setUrlDesc] = React.useState("")
  const [urlCategory, setUrlCategory] = React.useState("")
  const [urlSubcategory, setUrlSubcategory] = React.useState("")
  const [urlPublic, setUrlPublic] = React.useState(true)
  const [isSavingMutation, setIsSavingMutation] = React.useState(false)

  const resolveCategoryId = React.useCallback((value?: string) => {
    if (!value) return ""
    const byId = categories.find((category) => category.id === value)
    if (byId) return byId.id
    const byName = categories.find(
      (category) => category.name.toLowerCase() === value.toLowerCase(),
    )
    return byName?.id || ""
  }, [categories])

  const resolveSubcategoryId = React.useCallback((categoryId: string, value?: string) => {
    if (!categoryId || !value) return ""
    const category = categories.find((categoryOption) => categoryOption.id === categoryId)
    if (!category) return ""
    const byId = category.subcategories.find((subcategory) => subcategory.id === value)
    if (byId) return byId.id
    const byName = category.subcategories.find(
      (subcategory) => subcategory.name.toLowerCase() === value.toLowerCase(),
    )
    return byName?.id || ""
  }, [categories])

  const openDocumentUpload = React.useCallback(() => {
    if (onRequestDocumentUpload) {
      onRequestDocumentUpload()
      return
    }
    setEditingDoc(null)
    setDocName("")
    setDocDesc("")
    setDocCategory("")
    setDocSubcategory("")
    setDocType("pdf")
    setDocReplaceFile(null)
    setDocDialogOpen(true)
  }, [onRequestDocumentUpload])

  const openUrlUpload = React.useCallback(() => {
    if (onRequestURLUpload) {
      onRequestURLUpload()
      return
    }
    setEditingUrl(null)
    setUrlTitle("")
    setUrlUrl("")
    setUrlDesc("")
    setUrlCategory("")
    setUrlSubcategory("")
    setUrlPublic(true)
    setUrlDialogOpen(true)
  }, [onRequestURLUpload])

  React.useEffect(() => {
    if (externalItems) {
      setItems(externalItems)
    }
  }, [externalItems])

  React.useEffect(() => {
    if (!externalItems) return
    const hydratedAssignments: Record<string, string[]> = {}
    externalItems.forEach((item) => {
      if (item.kind === "folder") return
      if (!item.tagIds || item.tagIds.length === 0) return
      hydratedAssignments[item.id] = Array.from(new Set(item.tagIds))
    })
    setTagAssignments(hydratedAssignments)
  }, [externalItems])

  // tag assignments: item ID → array of tag IDs
  const [tagAssignments, setTagAssignments] = React.useState<Record<string, string[]>>(() => ({ ...INITIAL_TAG_ASSIGNMENTS }))
  const availableTags = React.useMemo<AvailableTag[]>(
    () => fetchedTags.map((tag) => ({ id: tag.id, name: tag.name, color: tag.color })),
    [fetchedTags],
  )

  // La pestaña «Documentos», hoy oculta, dejaba crear una etiqueta sin salir del
  // documento. Aqui solo se podian asignar las que ya existian: crear una
  // pasaba de un paso a cuatro -ir a Etiquetas, crearla, volver, asignarla-.
  // El criterio dice que nada frecuente puede costar mas pasos que antes.
  const toggleTagOnItemRef = React.useRef<((itemId: string, tagId: string) => Promise<void>) | null>(null)

  const createAndAssignTag = React.useCallback(async (itemId: string, name: string) => {
    const limpio = name.trim()
    if (!limpio) return
    // Mismos valores por defecto que usa la pestaña Etiquetas al crear una.
    const creada = await createTag({ name: limpio, color: '#64748b', tag_type: 'universal' })
    await refreshTags()
    if (creada?.id) await toggleTagOnItemRef.current?.(itemId, String(creada.id))
  }, [createTag, refreshTags])

  const toggleTagOnItem = React.useCallback(async (itemId: string, tagId: string) => {
    const current = tagAssignments[itemId] || []
    const hasTag = current.includes(tagId)
    const next = hasTag ? current.filter(id => id !== tagId) : [...current, tagId]

    // Optimistic UI update.
    setTagAssignments((prev) => ({ ...prev, [itemId]: next }))

    const item = items.find((candidate) => candidate.id === itemId)
    const backendDocumentId = item ? getBackendDocumentId(item) : null

    // Keep local-only behavior for non-backend items (e.g. local demo URLs/folders).
    if (!backendDocumentId) {
      return
    }

    try {
      if (hasTag) {
        await documentTagService.removeTagsFromDocument(backendDocumentId, [tagId])
      } else {
        const tag = availableTags.find((availableTag) => availableTag.id === tagId)
        if (!tag) {
          throw new Error("Tag metadata not found for assignment")
        }
        await documentTagService.assignTagsToDocument(backendDocumentId, [
          {
            tag_id: tag.id,
            tag_name: tag.name,
            tag_type: "universal",
          },
        ])
      }
    } catch {
      // Roll back optimistic state if persistence fails.
      setTagAssignments((prev) => ({ ...prev, [itemId]: current }))
      toast.error("Failed to persist document tags")
    }
  }, [availableTags, items, tagAssignments])

  React.useEffect(() => {
    toggleTagOnItemRef.current = toggleTagOnItem
  }, [toggleTagOnItem])

  const getItemTags = React.useCallback((itemId: string): AvailableTag[] => {
    const ids = tagAssignments[itemId] || []
    return ids.map(id => availableTags.find(t => t.id === id)).filter(Boolean) as AvailableTag[]
  }, [tagAssignments, availableTags])

  // sorting & filtering
  const [sortField, setSortField] = React.useState<SortField>("name")
  const [sortDir, setSortDir] = React.useState<SortDir>("asc")
  const [typeFilter, setTypeFilter] = React.useState<"all" | "folder" | "document" | "url">("all")

  const toggleSort = (field: SortField) => {
    if (sortField === field) setSortDir((d) => d === "asc" ? "desc" : "asc")
    else { setSortField(field); setSortDir("asc") }
  }

  /* computed */
  const breadcrumb = React.useMemo(() => {
    const trail: ContentItem[] = []
    let id = currentFolder
    while (id) {
      const f = items.find((i) => i.id === id)
      if (!f) break
      trail.unshift(f)
      id = f.parentId
    }
    return trail
  }, [currentFolder, items])

  const children = React.useMemo(() => {
    const q = search.toLowerCase().trim()
    let list = items.filter((i) => i.parentId === currentFolder)
    if (q) list = list.filter((i) => i.name.toLowerCase().includes(q) || (i.description || "").toLowerCase().includes(q))
    if (typeFilter !== "all") list = list.filter((i) => i.kind === typeFilter)
    // folders always first, then sort by selected field
    const m = sortDir === "asc" ? 1 : -1
    return list.sort((a, b) => {
      if (a.kind === "folder" && b.kind !== "folder") return -1
      if (a.kind !== "folder" && b.kind === "folder") return 1
      // within same group
      if (sortField === "name") return m * a.name.localeCompare(b.name)
      if (sortField === "type") {
        const at = a.kind === "url" ? "URL" : (a.fileType || "").toUpperCase()
        const bt = b.kind === "url" ? "URL" : (b.fileType || "").toUpperCase()
        return m * at.localeCompare(bt)
      }
      if (sortField === "size") {
        const parseSize = (s?: string) => { if (!s) return 0; const n = parseFloat(s); return s.includes("MB") ? n * 1024 : n }
        return m * (parseSize(a.size) - parseSize(b.size))
      }
      if (sortField === "date") {
        const da = a.addedDate ? new Date(a.addedDate).getTime() : 0
        const db = b.addedDate ? new Date(b.addedDate).getTime() : 0
        return m * (da - db)
      }
      if (sortField === "status") return m * (a.status || "").localeCompare(b.status || "")
      return 0
    })
  }, [items, currentFolder, search, sortField, sortDir, typeFilter])

  const folderCount = children.filter((c) => c.kind === "folder").length
  const fileCount = children.filter((c) => c.kind !== "folder").length
  const hasVisibleItemsWithAssignedTags = React.useMemo(
    () => children.some((item) => item.kind !== "folder" && (tagAssignments[item.id]?.length || 0) > 0),
    [children, tagAssignments],
  )
  const shouldWaitForTagHydration = tagsLoading && hasVisibleItemsWithAssignedTags
  const showLoadingState = loading || shouldWaitForTagHydration

  /* actions */
  const openFolder = (id: string) => { setCurrentFolder(id); setSearch("") }
  const goUp = () => {
    if (!currentFolder) return
    const cur = items.find((i) => i.id === currentFolder)
    setCurrentFolder(cur?.parentId ?? null)
    setSearch("")
  }

  const saveFolder = async () => {
    const nm = folderName.trim(); if (!nm) return
    const previousItems = items
    if (editingFolder) {
      setItems((p) => p.map((i) => i.id === editingFolder.id ? { ...i, name: nm } : i))
      if (onUpdateFolder) {
        try {
          await onUpdateFolder(editingFolder.id, { name: nm })
        } catch {
          setItems(previousItems)
          toast.error("Failed to update folder")
          return
        }
      }
    } else {
      if (onCreateFolder) {
        try {
          await onCreateFolder({ name: nm, parentId: currentFolder })
        } catch {
          toast.error("Failed to create folder")
          return
        }
      } else {
        setItems((p) => [...p, { id: uid(), kind: "folder", name: nm, parentId: currentFolder }])
      }
    }
    setFolderDialogOpen(false); setEditingFolder(null); setFolderName("")
  }

  const openNewFolder = () => { setEditingFolder(null); setFolderName(""); setFolderDialogOpen(true) }
  const openEditFolder = (f: ContentItem) => { setEditingFolder(f); setFolderName(f.name); setFolderDialogOpen(true) }


  const saveDoc = async () => {
    const nm = docName.trim(); if (!nm) return

    if (editingDoc) {
      if (onUpdateDocument) {
        const categoryOption = categories.find((category) => category.id === docCategory)
        const subcategoryOption = categoryOption?.subcategories.find(
          (subcategory) => subcategory.id === docSubcategory,
        )
        const previousDoc = editingDoc

        setItems((previousItems) => previousItems.map((item) => item.id === editingDoc.id ? {
          ...item,
          name: nm,
          description: docDesc || undefined,
          category: categoryOption?.name || undefined,
          subcategory: subcategoryOption?.name || undefined,
        } : item))

        setIsSavingMutation(true)
        try {
          await onUpdateDocument(editingDoc.id, {
            title: nm,
            description: docDesc || undefined,
            category: docCategory || null,
            subcategory: docSubcategory || null,
            file: docReplaceFile || undefined,
          })
          closeDocDialog()
          toast.success(docReplaceFile ? "Document replaced and re-embedding started" : "Document updated")
        } catch {
          setItems((previousItems) => previousItems.map((item) => item.id === previousDoc.id ? previousDoc : item))
          toast.error("Failed to update document")
        } finally {
          setIsSavingMutation(false)
        }
        return
      }

      // Update existing document
      const newSize = docReplaceFile ? `${(docReplaceFile.size / (1024 * 1024)).toFixed(1)} MB` : editingDoc.size
      const newFileType = docReplaceFile ? (docReplaceFile.name.split(".").pop()?.toLowerCase() || editingDoc.fileType) : editingDoc.fileType
      const newName = docReplaceFile ? (nm.includes(".") ? nm : `${nm}.${newFileType}`) : (nm.includes(".") ? nm : `${nm}.${editingDoc.fileType}`)
      setItems((p) => p.map((i) => i.id === editingDoc.id ? {
        ...i,
        name: newName,
        description: docDesc,
        category: categories.find((category) => category.id === docCategory)?.name || undefined,
        subcategory: categories.find((category) => category.id === docCategory)?.subcategories.find((subcategory) => subcategory.id === docSubcategory)?.name || undefined,
        fileType: newFileType, size: newSize,
        ...(docReplaceFile ? { status: "uploaded" as const, addedDate: new Date().toISOString() } : {}),
      } : i))
    } else {
      // Create new
      const sizes: Record<FileKind, string> = {
        pdf: `${(Math.random() * 5 + 0.2).toFixed(1)} MB`,
        txt: `${Math.round(Math.random() * 200 + 10)} KB`,
        csv: `${Math.round(Math.random() * 500 + 20)} KB`,
        doc: `${(Math.random() * 4 + 0.3).toFixed(1)} MB`,
      }
      setItems((p) => [...p, {
        id: uid(), kind: "document", name: nm.includes(".") ? nm : `${nm}.${docType}`, parentId: currentFolder,
        description: docDesc,
        category: categories.find((category) => category.id === docCategory)?.name || undefined,
        subcategory: categories.find((category) => category.id === docCategory)?.subcategories.find((subcategory) => subcategory.id === docSubcategory)?.name || undefined,
        fileType: docType,
        size: sizes[docType],
        status: "uploaded",
        addedDate: new Date().toISOString(),
      }])
    }
    closeDocDialog()
  }

  const closeDocDialog = () => {
    setDocDialogOpen(false); setEditingDoc(null); setDocName(""); setDocDesc(""); setDocCategory(""); setDocSubcategory(""); setDocType("pdf"); setDocReplaceFile(null)
  }

  const saveUrl = async () => {
    const nm = urlTitle.trim(); const u = urlUrl.trim(); if (!nm || !u) return

    if (editingUrl) {
      if (onUpdateURL) {
        const categoryOption = categories.find((category) => category.id === urlCategory)
        const subcategoryOption = categoryOption?.subcategories.find(
          (subcategory) => subcategory.id === urlSubcategory,
        )
        const previousUrl = editingUrl

        setItems((previousItems) => previousItems.map((item) => item.id === editingUrl.id ? {
          ...item,
          name: nm,
          url: u,
          description: urlDesc || undefined,
          category: categoryOption?.name || undefined,
          subcategory: subcategoryOption?.name || undefined,
          isPublic: urlPublic,
        } : item))

        setIsSavingMutation(true)
        try {
          await onUpdateURL(editingUrl.id, {
            title: nm,
            url: u,
            description: urlDesc || undefined,
            category: urlCategory || null,
            subcategory: urlSubcategory || null,
            isPublic: urlPublic,
          })
          closeUrlDialog()
          toast.success("URL updated")
        } catch {
          setItems((previousItems) => previousItems.map((item) => item.id === previousUrl.id ? previousUrl : item))
          toast.error("Failed to update URL")
        } finally {
          setIsSavingMutation(false)
        }
        return
      }

      setItems((p) => p.map((i) => i.id === editingUrl.id ? {
        ...i,
        name: nm,
        url: u,
        description: urlDesc,
        category: categories.find((category) => category.id === urlCategory)?.name || undefined,
        subcategory: categories.find((category) => category.id === urlCategory)?.subcategories.find((subcategory) => subcategory.id === urlSubcategory)?.name || undefined,
        isPublic: urlPublic,
      } : i))
    } else {
      setItems((p) => [...p, {
        id: uid(), kind: "url", name: nm, parentId: currentFolder,
        description: urlDesc,
        url: u,
        category: categories.find((category) => category.id === urlCategory)?.name || undefined,
        subcategory: categories.find((category) => category.id === urlCategory)?.subcategories.find((subcategory) => subcategory.id === urlSubcategory)?.name || undefined,
        isPublic: urlPublic,
        status: "processing",
        addedDate: new Date().toISOString(),
      }])
    }
    closeUrlDialog()
  }

  const closeUrlDialog = () => {
    setUrlDialogOpen(false); setEditingUrl(null); setUrlTitle(""); setUrlUrl(""); setUrlDesc(""); setUrlCategory(""); setUrlSubcategory(""); setUrlPublic(true)
  }

  const editItem = (item: ContentItem) => {
    if (item.kind === "document") {
      setEditingDoc(item)
      setDocName(item.name)
      setDocDesc(item.description || "")
      const mappedCategoryId = resolveCategoryId(item.category)
      setDocCategory(mappedCategoryId)
      setDocSubcategory(resolveSubcategoryId(mappedCategoryId, item.subcategory))
      setDocType((item.fileType as FileKind) || "pdf")
      setDocReplaceFile(null)
      setDocDialogOpen(true)
    } else if (item.kind === "url") {
      setEditingUrl(item)
      setUrlTitle(item.name)
      setUrlUrl(item.url || "")
      setUrlDesc(item.description || "")
      const mappedCategoryId = resolveCategoryId(item.category)
      setUrlCategory(mappedCategoryId)
      setUrlSubcategory(resolveSubcategoryId(mappedCategoryId, item.subcategory))
      setUrlPublic(item.isPublic ?? true)
      setUrlDialogOpen(true)
    }
  }

  /* ── Selection state (declared early so drag handlers can reference it) ── */
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set())

  /* ── Drag & Drop ──────────────────────────────────────── */
  const [draggingIds, setDraggingIds] = React.useState<Set<string>>(new Set())
  const [dropTargetId, setDropTargetId] = React.useState<string | null>(null)
  const dragGhostRef = React.useRef<HTMLDivElement | null>(null)

  const isDescendant = React.useCallback((itemId: string, potentialParentId: string): boolean => {
    let cur: string | null = potentialParentId
    while (cur) {
      if (cur === itemId) return true
      const parent = items.find(i => i.id === cur)
      cur = parent?.parentId ?? null
    }
    return false
  }, [items])

  const handleDragStart = React.useCallback((e: React.DragEvent, itemId: string) => {
    // If dragging a selected item, drag ALL selected; otherwise just this one
    const ids = selectedIds.has(itemId) && selectedIds.size > 1
      ? new Set(selectedIds)
      : new Set([itemId])
    setDraggingIds(ids)
    e.dataTransfer.effectAllowed = "move"
    e.dataTransfer.setData("text/plain", JSON.stringify(Array.from(ids)))

    // Clone actual card DOM nodes to build a stacked drag ghost
    const idArray = Array.from(ids)
    const cards: HTMLElement[] = []
    for (const id of idArray) {
      const el = document.querySelector(`[data-content-card="${id}"]`) as HTMLElement | null
      if (el) cards.push(el)
      if (cards.length >= 3) break // max 3 in the stack
    }

    if (cards.length === 0) return

    const count = ids.size
    const first = cards[0]
    const cardW = Math.min(first.offsetWidth, 220)
    const cardH = Math.min(first.offsetHeight, 200)
    const stackOffset = 8
    const totalW = cardW + stackOffset * (cards.length - 1) + 30 // +30 for badge overflow
    const totalH = cardH + stackOffset * (cards.length - 1) + 16

    const ghost = document.createElement("div")
    ghost.style.cssText = `position:fixed;top:-2000px;left:-2000px;pointer-events:none;z-index:99999;width:${totalW}px;height:${totalH}px;`

    const stack = document.createElement("div")
    stack.style.cssText = `position:relative;width:${totalW}px;height:${totalH}px;`

    // Detect dark mode for solid card backgrounds
    const isDark = document.documentElement.classList.contains("dark")
    const solidBg = isDark ? "#1a1f2e" : "#f0f2f5"

    // Render cards back-to-front (last card at back, first card on top)
    const clones = [...cards].reverse()
    clones.forEach((origCard, reverseIdx) => {
      const idx = cards.length - 1 - reverseIdx
      const clone = origCard.cloneNode(true) as HTMLElement
      const offset = idx * stackOffset
      const rotate = cards.length === 1 ? 0 : idx === 0 ? 0 : idx === 1 ? -2.5 : 2.5
      clone.style.cssText = `
        position:absolute; top:${offset}px; left:${offset}px;
        width:${cardW}px; height:${cardH}px;
        transform: rotate(${rotate}deg) scale(0.95);
        border-radius: 12px; overflow:hidden;
        background: ${solidBg};
        box-shadow: 0 8px 24px rgba(0,0,0,0.35), 0 2px 8px rgba(0,0,0,0.2);
        opacity: ${idx === 0 ? 1 : 0.92};
        pointer-events:none;
        border: 1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.1)"};
      `
      // Remove any hover/selection ring styles from the clone
      clone.classList.remove("ring-2", "ring-primary", "bg-primary/5")
      stack.appendChild(clone)
    })

    // Count badge (if multiple) — emerald green matching the app's accent
    if (count > 1) {
      const badge = document.createElement("div")
      badge.style.cssText = `
        position:absolute; top:-4px; right:12px; z-index:10;
        min-width:26px; height:26px; border-radius:13px; padding:0 7px;
        background:#4b5563; color:white;
        font-size:12px; font-weight:700; font-family:system-ui,sans-serif;
        display:flex; align-items:center; justify-content:center;
        box-shadow:0 2px 8px rgba(0,0,0,0.3);
        letter-spacing:0.02em;
      `
      badge.textContent = String(count)
      stack.appendChild(badge)
    }

    ghost.appendChild(stack)
    document.body.appendChild(ghost)
    dragGhostRef.current = ghost

    // Center the drag cursor on the top card
    const cursorX = Math.round(cardW / 2)
    const cursorY = Math.round(cardH / 2)
    e.dataTransfer.setDragImage(ghost, cursorX, cursorY)

    // Clean up ghost after browser captures the snapshot
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (dragGhostRef.current) {
          try { document.body.removeChild(dragGhostRef.current) } catch {
            // Ignore removeChild race conditions during drag teardown.
          }
          dragGhostRef.current = null
        }
      }, 0)
    })
  }, [selectedIds])

  const handleDragEnd = React.useCallback(() => {
    setDraggingIds(new Set())
    setDropTargetId(null)
    if (dragGhostRef.current) {
      try { document.body.removeChild(dragGhostRef.current) } catch {
        // Ignore removeChild race conditions during drag teardown.
      }
      dragGhostRef.current = null
    }
  }, [])

  const handleDragOver = React.useCallback((e: React.DragEvent, folderId: string) => {
    e.preventDefault()
    e.stopPropagation()
    if (draggingIds.size === 0 || draggingIds.has(folderId)) {
      e.dataTransfer.dropEffect = "none"
      return
    }
    // Check none of the dragged items are ancestors of the target
    for (const id of draggingIds) {
      if (isDescendant(id, folderId)) {
        e.dataTransfer.dropEffect = "none"
        return
      }
    }
    e.dataTransfer.dropEffect = "move"
    setDropTargetId(folderId)
  }, [draggingIds, isDescendant])

  const handleDragLeave = React.useCallback((e: React.DragEvent) => {
    const related = e.relatedTarget as HTMLElement | null
    if (related && e.currentTarget.contains(related)) return
    setDropTargetId(null)
  }, [])

  const handleDrop = React.useCallback((e: React.DragEvent, targetFolderId: string) => {
    e.preventDefault()
    e.stopPropagation()
    let ids: string[] = []
    try { ids = JSON.parse(e.dataTransfer.getData("text/plain")) } catch { return }
    if (!ids.length) return
    // Validate: none of the items is the target or ancestor of target
    for (const id of ids) {
      if (id === targetFolderId || isDescendant(id, targetFolderId)) return
    }
    const idSet = new Set(ids)
    const moved = items.filter((item) => idSet.has(item.id) && item.parentId !== targetFolderId)
    const previousItems = items
    if (moved.length === 0) return
    setItems(p => p.map(i => idSet.has(i.id) && i.parentId !== targetFolderId ? { ...i, parentId: targetFolderId } : i))
    if (onMoveItems) {
      void onMoveItems(moved.map((item) => ({ contentId: item.id, targetFolderId }))).catch(() => {
        setItems(previousItems)
        toast.error("Failed to move selected items")
      })
    }
    setDraggingIds(new Set())
    setDropTargetId(null)
    setSelectedIds(new Set())
  }, [isDescendant, items, onMoveItems])

  /* ── Selection & Delete Confirmation ─────────────────── */
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false)
  const [pendingDeleteIds, setPendingDeleteIds] = React.useState<string[]>([])

  const toggleSelect = React.useCallback((id: string, multi: boolean) => {
    setSelectedIds(prev => {
      const next = multi ? new Set(prev) : new Set<string>()
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }, [])

  const clearSelection = React.useCallback(() => setSelectedIds(new Set()), [])

  // Clear selection when navigating
  React.useEffect(() => { clearSelection() }, [currentFolder, clearSelection])

  const requestDelete = React.useCallback((ids: string[]) => {
    setPendingDeleteIds(ids)
    setDeleteConfirmOpen(true)
  }, [])

  const confirmDelete = React.useCallback(async () => {
    const toDelete = new Set<string>()
    const collect = (targetId: string) => {
      toDelete.add(targetId)
      items.filter((i) => i.parentId === targetId).forEach((c) => collect(c.id))
    }
    pendingDeleteIds.forEach(collect)
    const previousItems = items
    const previousFolder = currentFolder
    const previousSelected = new Set(selectedIds)
    setItems((p) => p.filter((i) => !toDelete.has(i.id)))
    if (currentFolder && toDelete.has(currentFolder)) setCurrentFolder(null)
    setDeleteConfirmOpen(false)
    setPendingDeleteIds([])
    setSelectedIds(prev => {
      const next = new Set(prev)
      toDelete.forEach(id => next.delete(id))
      return next
    })

    if (!onDeleteItems) return

    try {
      await onDeleteItems(Array.from(toDelete))
    } catch {
      setItems(previousItems)
      setCurrentFolder(previousFolder)
      setSelectedIds(previousSelected)
      toast.error("Failed to delete selected items")
    }
  }, [pendingDeleteIds, items, currentFolder, selectedIds, onDeleteItems])

  const bulkDelete = React.useCallback(() => {
    requestDelete(Array.from(selectedIds))
  }, [selectedIds, requestDelete])

  const bulkMoveToFolder = React.useCallback((targetFolderId: string) => {
    const moved = items.filter((item) => selectedIds.has(item.id) && item.parentId !== targetFolderId)
    if (moved.length === 0) {
      clearSelection()
      return
    }
    const previousItems = items
    setItems(p => p.map(i => selectedIds.has(i.id) ? { ...i, parentId: targetFolderId } : i))
    if (onMoveItems) {
      void onMoveItems(moved.map((item) => ({ contentId: item.id, targetFolderId }))).catch(() => {
        setItems(previousItems)
        toast.error("Failed to move selected items")
      })
    }
    clearSelection()
  }, [selectedIds, clearSelection, items, onMoveItems])

  // Wrap single delete to go through confirmation
  const handleSingleDelete = React.useCallback((id: string) => {
    requestDelete([id])
  }, [requestDelete])

  const handleDownloadClick = React.useCallback(async (item: ContentItem) => {
    if (item.kind !== "document") return

    const downloadUrl = resolveDownloadUrl(item)
    if (!downloadUrl) {
      sonnerToast.warning("No file available for download")
      return
    }

    const loadingToastId = sonnerToast.loading("Preparing your download...")

    try {
      const token = getAuthToken()
      const requestInit: RequestInit = { method: "GET" }
      if (token) {
        requestInit.headers = { Authorization: `Bearer ${token}` }
      }

      const response = await fetch(downloadUrl, requestInit)
      if (!response.ok) {
        throw new Error(`Failed to download (${response.status})`)
      }

      const blob = await response.blob()
      const objectUrl = URL.createObjectURL(blob)
      const contentDisposition = response.headers.get("content-disposition")
      const responseFilename = getFilenameFromContentDisposition(contentDisposition)
      const fallbackName =
        item.name?.trim() || item.filePath?.split("/").pop()?.trim() || "document"
      const filename = responseFilename || fallbackName

      const anchor = document.createElement("a")
      anchor.href = objectUrl
      anchor.download = filename
      anchor.rel = "noopener noreferrer"
      document.body.appendChild(anchor)
      anchor.click()
      document.body.removeChild(anchor)

      sonnerToast.dismiss(loadingToastId)
      sonnerToast.success("Download started.")

      setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000)
    } catch {
      sonnerToast.dismiss(loadingToastId)
      sonnerToast.error("Failed to download document")
    }
  }, [])

  // Available folders for "Move to" (excluding selected items and their descendants)
  const moveTargetFolders = React.useMemo(() => {
    return items.filter(i => i.kind === "folder" && !selectedIds.has(i.id))
  }, [items, selectedIds])

  /* ── Rubber-band selection ───────────────────────────── */
  const contentRef = React.useRef<HTMLDivElement>(null)
  const [rubberBand, setRubberBand] = React.useState<{ startX: number; startY: number; x: number; y: number } | null>(null)
  const rbStartRef = React.useRef<{ x: number; y: number; scrollTop: number } | null>(null)

  const handleRbMouseDown = React.useCallback((e: React.MouseEvent) => {
    // Only start rubber-band on the background, not on cards
    if ((e.target as HTMLElement).closest("[data-content-card]")) return
    if (e.button !== 0) return
    const rect = contentRef.current?.getBoundingClientRect()
    if (!rect) return
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top + (contentRef.current?.scrollTop || 0)
    rbStartRef.current = { x, y, scrollTop: contentRef.current?.scrollTop || 0 }
    if (!e.shiftKey && !e.metaKey && !e.ctrlKey) clearSelection()
  }, [clearSelection])

  const handleRbMouseMove = React.useCallback((e: React.MouseEvent) => {
    if (!rbStartRef.current || !contentRef.current) return
    const rect = contentRef.current.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top + contentRef.current.scrollTop
    const dx = Math.abs(x - rbStartRef.current.x)
    const dy = Math.abs(y - rbStartRef.current.y)
    if (dx < 5 && dy < 5 && !rubberBand) return // threshold
    setRubberBand({
      startX: rbStartRef.current.x,
      startY: rbStartRef.current.y - contentRef.current.scrollTop + (rbStartRef.current.scrollTop),
      x, y: y - contentRef.current.scrollTop + (contentRef.current.scrollTop),
    })
    // Hit-test cards
    const cards = contentRef.current.querySelectorAll("[data-content-card]")
    const minX = Math.min(rbStartRef.current.x, x) + rect.left
    const maxX = Math.max(rbStartRef.current.x, x) + rect.left
    const scrollDelta = contentRef.current.scrollTop - rbStartRef.current.scrollTop
    const minY = Math.min(rbStartRef.current.y - scrollDelta, y - scrollDelta) + rect.top - contentRef.current.scrollTop
    const maxY = Math.max(rbStartRef.current.y - scrollDelta, y - scrollDelta) + rect.top - contentRef.current.scrollTop
    const ids = new Set<string>()
    cards.forEach(card => {
      const cr = card.getBoundingClientRect()
      if (cr.right > minX && cr.left < maxX && cr.bottom > minY && cr.top < maxY) {
        const id = (card as HTMLElement).dataset.contentCard
        if (id) ids.add(id)
      }
    })
    setSelectedIds(ids)
  }, [rubberBand])

  const handleRbMouseUp = React.useCallback(() => {
    rbStartRef.current = null
    setRubberBand(null)
  }, [])

  /* ---- render ---- */

  return (
    <div className="h-full flex flex-col p-3 pt-3 md:p-6 md:pt-5">
      {loading && <ToolbarSkeleton isMobile={isMobile} view={view} />}

      {/* ─── Cabecera: donde estas ─────────────────────────────
          Lo mas importante de esta pantalla -en que carpeta estas- se anunciaba
          en el texto mas pequeno y mas apagado de todos, mientras que lo mas
          visible de la fila era una pildora con borde y un punto verde que ni
          siquiera es un boton. Jerarquia al reves: mandaba el adorno.

          Ahora el sitio es el titulo de la seccion -y con el la pantalla pasa a
          usar tres tamanos de los cuatro, que es lo que hace que el ojo sepa
          donde posarse- y el recuento es lo que acompana. La ruta va encima, en
          pequeno, porque es por donde has venido y no donde estas. Ver los
          criterios de jerarquia y de tipografia en
          harness/skills/muveran-design/SKILL.md. */}
      {!loading && !isMobile && (
        <div className="flex flex-wrap items-end justify-between gap-2 pb-4">
          <div className="min-w-0 space-y-1">
            {currentFolder && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Button variant="ghost" size="icon-xs" className="shrink-0" onClick={goUp}>
                  <ArrowLeft className="h-3.5 w-3.5" />
                </Button>
                {breadcrumb.slice(0, -1).map((f) => (
                  <React.Fragment key={f.id}>
                    <Button variant="link" size="xs" onClick={() => openFolder(f.id)} className="truncate text-muted-foreground">
                      {f.name}
                    </Button>
                    <ChevronRight className="h-3 w-3 shrink-0" />
                  </React.Fragment>
                ))}
              </div>
            )}

            <h2 className="truncate text-lg font-semibold">
              {currentFolder
                ? breadcrumb[breadcrumb.length - 1]?.name ?? t("knowledge.tabs.content")
                : t("knowledge.tabs.content")}
            </h2>

            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
              <span>
                {folderCount > 0 && <>{folderCount} {folderCount === 1 ? t("knowledge.content.folder") : t("knowledge.content.folders")}</>}
                {folderCount > 0 && fileCount > 0 && <span className="mx-1.5">·</span>}
                {fileCount > 0 && <>{fileCount} {fileCount === 1 ? t("knowledge.content.file") : t("knowledge.content.files")}</>}
                {folderCount === 0 && fileCount === 0 && t("knowledge.content.empty")}
              </span>
              {!currentFolder && stats && <StatsBadge stats={stats} />}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("knowledge.content.search")}
                className="h-9 w-40 sm:w-56 pl-9 text-sm bg-muted/30 border-transparent focus:border-border focus:bg-background transition-colors"
              />
            </div>

            <div className="flex items-center rounded-lg border bg-muted/30 p-0.5">
              <Button
                variant="ghost"
                size="icon-xs"
                className={cn("rounded-md", view === "grid" && "bg-background shadow-sm text-foreground")}
                onClick={() => setView("grid")}
              >
                <LayoutGrid className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon-xs"
                className={cn("rounded-md", view === "list" && "bg-background shadow-sm text-foreground")}
                onClick={() => setView("list")}
              >
                <List className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Row 2 · Actions & Filter chips ──────────────────── */}
      {!loading && (
        <div className="flex flex-wrap items-center gap-2 pb-5 md:gap-2 md:flex-nowrap">
        {/* primary CTA */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" className="gap-2 font-semibold shadow-sm shrink-0">
              <Plus className="h-4 w-4" />
              {!isMobile && t("knowledge.content.addNew")}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-48">
            <DropdownMenuItem onClick={openNewFolder} className="gap-2">
              <Folder className="h-4 w-4 text-warning" />{t("knowledge.content.newFolder")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={openDocumentUpload} className="gap-2">
              <Upload className="h-4 w-4 text-muted-foreground" />{t("knowledge.content.uploadDoc")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={openUrlUpload} className="gap-2">
              <Link2 className="h-4 w-4 text-muted-foreground" />{t("knowledge.content.addUrl")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* secondary actions — desktop only */}
        {!isMobile && (
          <>
            <Button size="sm" variant="ghost" className="gap-2 text-muted-foreground hover:text-foreground" onClick={openDocumentUpload}>
              <Upload className="h-4 w-4" />{t("knowledge.content.uploadDoc")}
            </Button>
            <Button size="sm" variant="ghost" className="gap-2 text-muted-foreground hover:text-foreground" onClick={openUrlUpload}>
              <Link2 className="h-4 w-4" />{t("knowledge.content.addUrl")}
            </Button>
          </>
        )}

        {!isMobile && <div className="flex-1" />}

        {/* filter chips */}
        <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as FilterKind)}>
          <SelectTrigger className={cn("h-8 gap-1 rounded-full border-dashed text-xs text-muted-foreground hover:text-foreground transition-colors shrink-0 px-2", isMobile ? "w-[4.5rem]" : "w-auto min-w-[5.5rem]")}>
            <SelectValue>{isMobile ? (typeFilter === "all" ? "All" : typeFilter === "folder" ? "Folders" : typeFilter === "document" ? "Docs" : "URLs") : undefined}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("knowledge.content.filterAll")}</SelectItem>
            <SelectItem value="folder">{t("knowledge.content.filterFolders")}</SelectItem>
            <SelectItem value="document">{t("knowledge.content.filterDocs")}</SelectItem>
            <SelectItem value="url">{t("knowledge.content.filterUrls")}</SelectItem>
          </SelectContent>
        </Select>

        <Select value={`${sortField}_${sortDir}`} onValueChange={(v) => { const [f, d] = v.split("_") as [SortField, SortDir]; setSortField(f); setSortDir(d) }}>
          <SelectTrigger className={cn("h-8 gap-1 rounded-full border-dashed text-xs text-muted-foreground hover:text-foreground transition-colors shrink-0 px-2", isMobile ? "w-[3.5rem]" : "w-auto min-w-[8rem]")}>
            <SelectValue>{isMobile ? (sortDir === "asc" && sortField === "name" ? "A-Z" : sortDir === "desc" && sortField === "name" ? "Z-A" : sortDir === "desc" && sortField === "date" ? "New" : sortDir === "asc" && sortField === "date" ? "Old" : sortField === "type" ? "Type" : "Size") : undefined}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="name_asc">{t("knowledge.content.sortNameAZ")}</SelectItem>
            <SelectItem value="name_desc">{t("knowledge.content.sortNameZA")}</SelectItem>
            <SelectItem value="date_desc">{t("knowledge.content.sortDateNew")}</SelectItem>
            <SelectItem value="date_asc">{t("knowledge.content.sortDateOld")}</SelectItem>
            <SelectItem value="type_asc">{t("knowledge.content.sortType")}</SelectItem>
            <SelectItem value="size_desc">{t("knowledge.content.sortSize")}</SelectItem>
          </SelectContent>
        </Select>

        {isMobile && <div className="flex-1" />}

        {/* mobile: search icon button + expandable */}
        {isMobile ? (
          mobileSearchOpen ? (
            <div className="relative shrink-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onBlur={() => { if (!search) setMobileSearchOpen(false) }}
                placeholder={t("knowledge.content.search")}
                className="h-9 w-36 pl-9 text-sm bg-muted/30 border-transparent focus:border-border focus:bg-background transition-colors"
              />
            </div>
          ) : (
            <Button variant="ghost" size="icon-sm" className="shrink-0" onClick={() => setMobileSearchOpen(true)}>
              <Search className="h-4 w-4" />
            </Button>
          )
        ) : null}

        {/* mobile: view toggle at far right */}
        {isMobile && (
          <div className="flex items-center rounded-lg border bg-muted/30 p-0.5 shrink-0">
            <Button variant="ghost" size="icon-xs" className={cn("rounded-md", view === "grid" && "bg-background shadow-sm text-foreground")} onClick={() => setView("grid")}>
              <LayoutGrid className="h-3.5 w-3.5" />
            </Button>
            <Button variant="ghost" size="icon-xs" className={cn("rounded-md", view === "list" && "bg-background shadow-sm text-foreground")} onClick={() => setView("list")}>
              <List className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
        </div>
      )}

      {/* content area with rubber-band selection */}
      <div
        ref={contentRef}
        className="flex-1 overflow-auto relative select-none p-2"
        onMouseDown={handleRbMouseDown}
        onMouseMove={handleRbMouseMove}
        onMouseUp={handleRbMouseUp}
        onMouseLeave={handleRbMouseUp}
      >
        {showLoadingState ? (
          view === "grid" ? (
            <GridLoadingState count={Math.max(children.length, 6)} />
          ) : (
            <ListLoadingState count={Math.max(children.length, 6)} />
          )
        ) : view === "grid" ? (
          <GridView items={children} allItems={items} onOpenFolder={openFolder} onEdit={editItem} onEditFolder={openEditFolder} onDelete={handleSingleDelete} onDownload={handleDownloadClick}
            draggingIds={draggingIds} dropTargetId={dropTargetId}
            onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
            selectedIds={selectedIds} onToggleSelect={toggleSelect}
            tagAssignments={tagAssignments} onToggleTag={toggleTagOnItem} onCreateTag={createAndAssignTag} getItemTags={getItemTags} availableTags={availableTags} />
        ) : (
          <ListView items={children} onOpenFolder={openFolder} onEdit={editItem} onEditFolder={openEditFolder} onDelete={handleSingleDelete} onDownload={handleDownloadClick} sortField={sortField} sortDir={sortDir} onSort={toggleSort}
            draggingIds={draggingIds} dropTargetId={dropTargetId}
            onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
            selectedIds={selectedIds} onToggleSelect={toggleSelect}
            tagAssignments={tagAssignments} onToggleTag={toggleTagOnItem} onCreateTag={createAndAssignTag} getItemTags={getItemTags} availableTags={availableTags} />
        )}

        {/* Rubber-band rectangle */}
        {rubberBand && (
          <div
            className="absolute border-2 border-primary/50 bg-primary/10 rounded-md pointer-events-none z-20"
            style={{
              left: Math.min(rubberBand.startX, rubberBand.x),
              top: Math.min(rubberBand.startY, rubberBand.y),
              width: Math.abs(rubberBand.x - rubberBand.startX),
              height: Math.abs(rubberBand.y - rubberBand.startY),
            }}
          />
        )}
      </div>

      {/* ── Floating bulk action bar ─────────────────────── */}
      {selectedIds.size > 0 && (() => {
        const selArr = Array.from(selectedIds)
        const taggableIds = selArr.filter(id => { const it = items.find(i => i.id === id); return it && it.kind !== "folder" })
        const downloadableIds = taggableIds // same set — non-folders

        return (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex max-w-[calc(100vw-1rem)] flex-wrap items-center justify-center gap-2 rounded-lg border bg-background shadow-md px-5 py-3 animate-in fade-in slide-in-from-bottom-4 duration-200">
            <span className="text-sm font-medium">{selectedIds.size} selected</span>
            <div className="h-5 w-px bg-border/60" />

            {/* Download selected (non-folder items) */}
            {downloadableIds.length > 0 && (
              <Button variant="ghost" size="xs" className="gap-2 text-primary/80 hover:text-primary hover:bg-primary/10" onClick={() => {/* mock download */}}>
                <Download className="h-3.5 w-3.5" /> Download ({downloadableIds.length})
              </Button>
            )}

            {/* Bulk tag assignment */}
            {taggableIds.length > 0 && (
              <BulkTagPopover
                itemIds={taggableIds}
                tagAssignments={tagAssignments}
                onToggleTag={toggleTagOnItem}
                availableTags={availableTags}
              />
            )}

            {/* Move to folder */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="xs" className="gap-2">
                  <FolderInput className="h-3.5 w-3.5" /> Move to
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-48 max-h-60 overflow-auto">
                {moveTargetFolders.length === 0 ? (
                  <DropdownMenuItem disabled>No folders available</DropdownMenuItem>
                ) : moveTargetFolders.map(f => (
                  <DropdownMenuItem key={f.id} onClick={() => bulkMoveToFolder(f.id)} className="gap-2">
                    <Folder className="h-4 w-4 text-warning" />{f.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Bulk delete */}
            <Button variant="ghost" size="xs" className="gap-2 text-destructive hover:text-destructive" onClick={bulkDelete}>
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </Button>

            <div className="h-5 w-px bg-border/60" />
            <Button variant="ghost" size="icon-xs" className="text-muted-foreground hover:text-foreground" onClick={clearSelection}>
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        )
      })()}

      {/* ── Delete confirmation dialog ───────────────────── */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Are you sure?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDeleteIds.length === 1
                ? `This will permanently delete "${items.find(i => i.id === pendingDeleteIds[0])?.name || "this item"}". This action cannot be undone.`
                : `This will permanently delete ${pendingDeleteIds.length} items. This action cannot be undone.`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive hover:bg-destructive text-white">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ---- Dialogs ---- */}

      {/* Folder dialog */}
      <Dialog open={folderDialogOpen} onOpenChange={(v) => { setFolderDialogOpen(v); if (!v) setEditingFolder(null) }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingFolder ? t("knowledge.content.editFolder") : t("knowledge.content.newFolder")}</DialogTitle>
            <DialogDescription>{t("knowledge.content.folderHint")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2"><Label>{t("knowledge.content.folderName")}</Label><Input value={folderName} onChange={(e) => setFolderName(e.target.value)} placeholder="e.g. Marketing" autoFocus onKeyDown={(e) => e.key === "Enter" && saveFolder()} /></div>
          <DialogFooter><Button variant="secondary" onClick={() => setFolderDialogOpen(false)}>{t("knowledge.content.cancel")}</Button><Button onClick={saveFolder} disabled={!folderName.trim()}>{t("knowledge.content.save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document upload / edit dialog */}
      <Dialog open={docDialogOpen} onOpenChange={(v) => { if (!v) closeDocDialog(); else setDocDialogOpen(v) }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingDoc ? <><Pencil className="h-4 w-4 shrink-0" /> Edit Document</> : t("knowledge.content.uploadDoc")}
            </DialogTitle>
            <DialogDescription>
              {editingDoc ? "Update the document details or replace the file." : t("knowledge.content.uploadDocHint")}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 min-w-0">
            {/* Read-only info when editing */}
            {editingDoc && (
              <div className="flex items-center gap-2 rounded-lg border bg-muted/30 px-4 py-3 min-w-0">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted/60 shrink-0">
                  {getFileIcon(editingDoc)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{editingDoc.name}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {(editingDoc.fileType || "").toUpperCase()} · {editingDoc.size} · {editingDoc.addedDate ? new Date(editingDoc.addedDate).toLocaleDateString() : "—"}
                  </p>
                </div>
                {editingDoc.status && (
                  <Badge variant="outline" className={cn("text-[10px] px-1.5 py-0 shrink-0", statusColor(editingDoc.status))}>
                    {editingDoc.status}
                  </Badge>
                )}
              </div>
            )}
            {/* Como lo trata el agente, y dejar de usarlo. Es lo que
                sobrevive del Workspace, y vive aqui porque aqui esta el
                documento. Va dentro del dialogo y no en la fila: la fila
                esta escrita dos veces -rejilla y lista- y ponerlo ahi seria
                escribirlo dos veces. */}
            {editingDoc && <ComoLoTrataElAgente documentoId={idDeDocumento(editingDoc.id)} />}
            {editingDoc && <WhoUsesIt documentId={idDeDocumento(editingDoc.id)} />}
            <div className="space-y-2"><Label>{t("knowledge.content.docTitle")}</Label><Input value={docName} onChange={(e) => setDocName(e.target.value)} placeholder="e.g. Getting Started Guide" autoFocus /></div>
            <div className="space-y-2"><Label>{t("knowledge.content.docDescription")}</Label><Textarea value={docDesc} onChange={(e) => setDocDesc(e.target.value)} placeholder={t("knowledge.content.docDescPlaceholder")} rows={2} /></div>
            <div className="grid grid-cols-2 gap-4">
              {!editingDoc ? (
                <div className="space-y-2">
                  <Label>{t("knowledge.content.docType")}</Label>
                  <Select value={docType} onValueChange={(v) => setDocType(v as FileKind)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>
                    <SelectItem value="pdf">PDF</SelectItem><SelectItem value="txt">TXT</SelectItem><SelectItem value="csv">CSV</SelectItem><SelectItem value="doc">DOC</SelectItem>
                  </SelectContent></Select>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Input value={(docReplaceFile ? (docReplaceFile.name.split(".").pop() || editingDoc.fileType || "") : (editingDoc.fileType || "")).toUpperCase()} disabled className="h-10 text-sm bg-muted/30" />
                </div>
              )}
              <div className="space-y-2">
                <Label>{t("knowledge.content.docCategory")}</Label>
                <Select value={docCategory || "__none__"} onValueChange={(value) => {
                  if (value === "__none__") {
                    setDocCategory("")
                    setDocSubcategory("")
                    return
                  }
                  setDocCategory(value)
                  setDocSubcategory("")
                }}>
                  <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No category</SelectItem>
                    {categories.map((category) => (
                      <SelectItem key={category.id} value={category.id}>{category.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Subcategory</Label>
              <Select value={docSubcategory} onValueChange={setDocSubcategory} disabled={!docCategory}>
                <SelectTrigger><SelectValue placeholder="Select subcategory" /></SelectTrigger>
                <SelectContent>
                  {(categories.find((category) => category.id === docCategory)?.subcategories || []).map((subcategory) => (
                    <SelectItem key={subcategory.id} value={subcategory.id}>{subcategory.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Current file + optional replace — only in edit mode */}
            {editingDoc && (
              <div className="space-y-2 min-w-0">
                <Label>File</Label>
                {docReplaceFile ? (
                  <>
                    {/* New file selected */}
                    <div className="flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 min-w-0">
                      <FileUp className="h-5 w-5 text-primary shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{docReplaceFile.name}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {(docReplaceFile.name.split(".").pop() || "").toUpperCase()} · {docReplaceFile.size >= 1024 * 1024 ? `${(docReplaceFile.size / (1024 * 1024)).toFixed(1)} MB` : `${(docReplaceFile.size / 1024).toFixed(0)} KB`}
                        </p>
                      </div>
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-primary/30 text-primary shrink-0">New</Badge>
                    </div>
                    {/* Undo: go back to original */}
                    <Button
                      variant="link"
                      size="xs"
                      onClick={() => setDocReplaceFile(null)}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <RefreshCw className="h-3 w-3" />
                      Keep original file instead
                    </Button>
                  </>
                ) : (
                  <>
                    {/* Current / original file */}
                    <div className="flex items-center gap-2 rounded-lg border bg-muted/20 px-4 py-3 min-w-0">
                      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted/60 shrink-0">
                        {getFileIcon(editingDoc)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{editingDoc.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{(editingDoc.fileType || "").toUpperCase()} · {editingDoc.size}</p>
                      </div>
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 shrink-0">Current</Badge>
                    </div>
                    {/* Optional: replace */}
                    <label className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground cursor-pointer transition-colors">
                      <Upload className="h-3 w-3 shrink-0" />
                      <span>Replace with a different file</span>
                      <input
                        type="file"
                        className="hidden"
                        accept=".pdf,.doc,.docx,.txt,.csv,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg"
                        onChange={(e) => { if (e.target.files?.[0]) setDocReplaceFile(e.target.files[0]) }}
                      />
                    </label>
                  </>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={closeDocDialog} disabled={isSavingMutation}>{t("knowledge.content.cancel")}</Button>
            <Button onClick={saveDoc} disabled={!docName.trim() || isSavingMutation}>
              {isSavingMutation ? "Saving..." : editingDoc ? "Save Changes" : t("knowledge.content.upload")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* URL add / edit dialog */}
      <Dialog open={urlDialogOpen} onOpenChange={(v) => { if (!v) closeUrlDialog(); else setUrlDialogOpen(v) }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingUrl ? <><Pencil className="h-4 w-4 shrink-0" /> Edit URL</> : t("knowledge.content.addUrl")}
            </DialogTitle>
            <DialogDescription>
              {editingUrl ? "Update the URL details." : t("knowledge.content.addUrlHint")}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 min-w-0">
            {/* Read-only info when editing */}
            {editingUrl && (
              <div className="flex items-center gap-2 rounded-lg border bg-muted/30 px-4 py-3 min-w-0">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 shrink-0">
                  <Globe className="h-5 w-5 text-primary/80" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{editingUrl.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{editingUrl.url}</p>
                </div>
                {editingUrl.status && (
                  <Badge variant="outline" className={cn("text-[10px] px-1.5 py-0 shrink-0", statusColor(editingUrl.status))}>
                    {editingUrl.status}
                  </Badge>
                )}
              </div>
            )}
            <div className="space-y-2"><Label>{t("knowledge.content.urlTitle")}</Label><Input value={urlTitle} onChange={(e) => setUrlTitle(e.target.value)} placeholder="e.g. Company Blog" autoFocus /></div>
            <div className="space-y-2"><Label>{t("knowledge.content.urlUrl")}</Label><Input value={urlUrl} onChange={(e) => setUrlUrl(e.target.value)} placeholder="https://example.com" /></div>
            <div className="space-y-2"><Label>{t("knowledge.content.urlDescription")}</Label><Textarea value={urlDesc} onChange={(e) => setUrlDesc(e.target.value)} placeholder={t("knowledge.content.urlDescPlaceholder")} rows={2} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("knowledge.content.docCategory")}</Label>
                <Select value={urlCategory || "__none__"} onValueChange={(value) => {
                  if (value === "__none__") {
                    setUrlCategory("")
                    setUrlSubcategory("")
                    return
                  }
                  setUrlCategory(value)
                  setUrlSubcategory("")
                }}>
                  <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No category</SelectItem>
                    {categories.map((category) => (
                      <SelectItem key={category.id} value={category.id}>{category.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Subcategory</Label>
                <Select value={urlSubcategory} onValueChange={setUrlSubcategory} disabled={!urlCategory}>
                  <SelectTrigger><SelectValue placeholder="Select subcategory" /></SelectTrigger>
                  <SelectContent>
                    {(categories.find((category) => category.id === urlCategory)?.subcategories || []).map((subcategory) => (
                      <SelectItem key={subcategory.id} value={subcategory.id}>{subcategory.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Label>{t("knowledge.content.urlVisibility")}</Label>
              <Select value={urlPublic ? "public" : "private"} onValueChange={(v) => setUrlPublic(v === "public")}><SelectTrigger className="w-32"><SelectValue /></SelectTrigger><SelectContent>
                <SelectItem value="public">{t("knowledge.content.public")}</SelectItem><SelectItem value="private">{t("knowledge.content.private")}</SelectItem>
              </SelectContent></Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={closeUrlDialog} disabled={isSavingMutation}>{t("knowledge.content.cancel")}</Button>
            <Button onClick={saveUrl} disabled={!urlTitle.trim() || !urlUrl.trim() || isSavingMutation}>
              {isSavingMutation ? "Saving..." : editingUrl ? "Save Changes" : t("knowledge.content.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function ToolbarSkeleton({ isMobile, view }: { isMobile: boolean; view: "grid" | "list" }) {
  return (
    <>
      {!isMobile && (
        <div className="flex flex-wrap items-center gap-2 pb-4">
          <div className="flex items-center gap-2">
            <Skeleton className="h-7 w-40 rounded-full" />
            <Skeleton className="h-4 w-32" />
          </div>
          <div className="flex-1" />
          <Skeleton className="h-9 w-56 rounded-md" />
          <div className="flex items-center rounded-lg border bg-muted/30 p-0.5">
            <Skeleton className="h-7 w-7 rounded-md" />
            <Skeleton className="h-7 w-7 rounded-md" />
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 md:gap-2 pb-5 flex-nowrap">
        <Skeleton className="h-9 w-32 rounded-md shrink-0" />

        {!isMobile && (
          <>
            <Skeleton className="h-9 w-32 rounded-md shrink-0" />
            <Skeleton className="h-9 w-28 rounded-md shrink-0" />
          </>
        )}

        {!isMobile && <div className="flex-1" />}

        <Skeleton className={cn("h-8 rounded-full shrink-0", isMobile ? "w-[4.5rem]" : "w-24")} />
        <Skeleton className={cn("h-8 rounded-full shrink-0", isMobile ? "w-[3.5rem]" : "w-32")} />

        {isMobile && <div className="flex-1" />}
        {isMobile ? (
          <>
            <Skeleton className="h-9 w-9 rounded-md shrink-0" />
            <div className="flex items-center rounded-lg border bg-muted/30 p-0.5 shrink-0">
              <Skeleton className={cn("h-7 w-7 rounded-md", view === "grid" && "opacity-100")} />
              <Skeleton className={cn("h-7 w-7 rounded-md", view === "list" && "opacity-100")} />
            </div>
          </>
        ) : null}
      </div>
    </>
  )
}

function GridLoadingState({ count }: { count: number }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
      {Array.from({ length: count }).map((_, index) => (
        <div key={`content-grid-loading-${index}`} className="overflow-hidden rounded-lg border bg-card/40">
          <div className="h-28 animate-pulse bg-muted/40" />
          <div className="space-y-2 p-3">
            <Skeleton className="h-4 w-4/5" />
            <div className="flex items-center gap-2">
              <Skeleton className="h-3 w-2/5" />
              <Skeleton className="h-4 w-12 rounded-full" />
            </div>
            <div className="flex items-center gap-1 pt-1">
              <Skeleton className="h-5 w-12 rounded-full" />
              <Skeleton className="h-5 w-10 rounded-full" />
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

function ListLoadingState({ count }: { count: number }) {
  return (
    <div className="rounded-lg border overflow-hidden">
      <div className="divide-y">
        {Array.from({ length: count }).map((_, index) => (
          <div key={`content-list-loading-${index}`} className="flex items-center gap-4 px-4 py-3">
            <Skeleton className="h-8 w-8 shrink-0 rounded-md" />
            <div className="min-w-0 flex-1 space-y-2">
              <Skeleton className="h-4 w-1/3" />
              <div className="flex items-center gap-2">
                <Skeleton className="h-3 w-1/4" />
                <Skeleton className="h-4 w-12 rounded-full" />
              </div>
            </div>
            <Skeleton className="h-6 w-16 rounded-full" />
          </div>
        ))}
      </div>
    </div>
  )
}

/* ================================================================== */
/*  Grid view                                                          */
/* ================================================================== */

interface DragProps {
  draggingIds: Set<string>
  dropTargetId: string | null
  onDragStart: (e: React.DragEvent, id: string) => void
  onDragEnd: (e: React.DragEvent) => void
  onDragOver: (e: React.DragEvent, folderId: string) => void
  onDragLeave: (e: React.DragEvent) => void
  onDrop: (e: React.DragEvent, folderId: string) => void
}

interface SelectProps {
  selectedIds: Set<string>
  onToggleSelect: (id: string, multi: boolean) => void
}

/* ------------------------------------------------------------------ */
/*  Tag Selector Popover                                               */
/* ------------------------------------------------------------------ */

function TagPill({ name, color, size = "sm" }: { name: string; color: string; size?: "sm" | "md" }) {
  const sizes = { sm: "text-[10px] px-1.5 py-px gap-1", md: "text-[11px] px-2 py-0.5 gap-2" }
  const dotSizes = { sm: "h-1.5 w-1.5", md: "h-1.5 w-1.5" }
  return (
    <span className={cn("inline-flex items-center rounded-full font-medium whitespace-nowrap", sizes[size])}
      style={{ backgroundColor: `${color}18`, color }}>
      <span className={cn("rounded-full shrink-0", dotSizes[size])} style={{ backgroundColor: color }} />
      {name}
    </span>
  )
}

function TagSelectorPopover({ itemId, assignedTagIds, onToggleTag, onCreateTag, availableTags, children }: {
  itemId: string
  assignedTagIds: string[]
  onToggleTag: (itemId: string, tagId: string) => void
  onCreateTag?: (itemId: string, name: string) => void | Promise<void>
  availableTags: AvailableTag[]
  children: React.ReactNode
}) {
  const { t } = useTranslation()
  const [search, setSearch] = React.useState("")
  const [open, setOpen] = React.useState(false)
  const inputRef = React.useRef<HTMLInputElement>(null)

  const filtered = React.useMemo(() => {
    const q = search.toLowerCase().trim()
    if (!q) return availableTags
    return availableTags.filter(t => t.name.toLowerCase().includes(q))
  }, [search, availableTags])

  // Focus search when opening
  React.useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50)
    } else {
      setSearch("")
    }
  }, [open])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {children}
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="start"
        className="w-64 p-0 overflow-hidden rounded-lg shadow-md border-border"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-3 pt-3 pb-2">
          <div className="flex items-center gap-2 mb-2">
            <Tag className="h-3.5 w-3.5 text-primary" />
            <span className="text-xs font-semibold">{t('knowledge.assign_tags')}</span>
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
            <Input
              ref={inputRef}
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('knowledge.search_tags')}
              className="h-8 border-0 bg-muted/50 pl-7 text-xs"
            />
          </div>
        </div>

        {/* Tag List */}
        <div className="max-h-52 overflow-y-auto px-1.5 pb-1.5">
          {filtered.length === 0 ? (
            <div className="py-6 text-center">
              {/* Antes solo decia «no hay». Crear una etiqueta obligaba a irse a
                  otra pestaña, crearla, volver y asignarla: cuatro pasos para lo
                  que la pestaña «Documentos» hacia en uno. */}
              {onCreateTag && search.trim() ? (
                <Button
                  variant="ghost"
                  size="xs"
                  className="mx-auto text-primary"
                  onClick={() => {
                    void onCreateTag(itemId, search.trim())
                    setOpen(false)
                  }}
                >
                  <Plus className="h-3 w-3" />
                  {t('knowledge.create_and_assign_tag', { name: search.trim() })}
                </Button>
              ) : (
                <p className="text-xs text-muted-foreground">{t('knowledge.no_tags_found')}</p>
              )}
            </div>
          ) : (
            <div className="space-y-1">
              {filtered.map(tag => {
                const isAssigned = assignedTagIds.includes(tag.id)
                return (
                  <Button
                    key={tag.id}
                    variant="ghost"
                    size="xs"
                    onClick={(e) => {
                      e.stopPropagation()
                      onToggleTag(itemId, tag.id)
                    }}
                    className={cn(
                      "w-full justify-start rounded-lg text-left",
                      isAssigned && "bg-primary/10"
                    )}
                  >
                    {/* Color dot */}
                    <span
                      className="h-3 w-3 rounded-full shrink-0 ring-1 ring-black/5 transition-transform duration-150"
                      style={{
                        backgroundColor: tag.color,
                        transform: isAssigned ? "scale(1.15)" : "scale(1)",
                      }}
                    />
                    {/* Tag name */}
                    <span className={cn(
                      "flex-1 text-xs truncate transition-colors",
                      isAssigned ? "font-medium text-foreground" : "text-muted-foreground"
                    )}>
                      {tag.name}
                    </span>
                    {/* Check indicator */}
                    <div className={cn(
                      "flex items-center justify-center h-4 w-4 rounded-md shrink-0 transition-all duration-200",
                      isAssigned
                        ? "bg-primary text-primary-foreground scale-100"
                        : "bg-muted/60 scale-90 opacity-0"
                    )}>
                      <Check className="h-2.5 w-2.5" />
                    </div>
                  </Button>
                )
              })}
            </div>
          )}
        </div>

        {/* Footer with count */}
        {assignedTagIds.length > 0 && (
          <div className="border-t px-3 py-2 bg-muted/20">
            <div className="flex items-center gap-2 flex-wrap">
              {assignedTagIds.slice(0, 5).map(tid => {
                const tag = availableTags.find(t => t.id === tid)
                if (!tag) return null
                return <TagPill key={tag.id} name={tag.name} color={tag.color} />
              })}
              {assignedTagIds.length > 5 && (
                <span className="text-[10px] text-muted-foreground">+{assignedTagIds.length - 5}</span>
              )}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}

/** Bulk tag popover for the floating action bar — applies/removes a tag from all selected items at once */
function BulkTagPopover({ itemIds, tagAssignments, onToggleTag, availableTags }: {
  itemIds: string[]
  tagAssignments: Record<string, string[]>
  onToggleTag: (itemId: string, tagId: string) => void
  onCreateTag?: (itemId: string, name: string) => void | Promise<void>
  availableTags: AvailableTag[]
}) {
  const { t } = useTranslation()
  const [search, setSearch] = React.useState("")
  const [open, setOpen] = React.useState(false)
  const inputRef = React.useRef<HTMLInputElement>(null)

  const filtered = React.useMemo(() => {
    const q = search.toLowerCase().trim()
    if (!q) return availableTags
    return availableTags.filter(t => t.name.toLowerCase().includes(q))
  }, [search, availableTags])

  // For each tag, compute how many of the selected items have it
  const tagCounts = React.useMemo(() => {
    const counts: Record<string, number> = {}
    availableTags.forEach(tag => {
      counts[tag.id] = itemIds.filter(id => (tagAssignments[id] || []).includes(tag.id)).length
    })
    return counts
  }, [itemIds, tagAssignments, availableTags])

  React.useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50)
    else setSearch("")
  }, [open])

  const handleToggle = (tagId: string) => {
    const count = tagCounts[tagId] || 0
    if (count === itemIds.length) {
      // All have it → remove from all
      itemIds.forEach(id => {
        if ((tagAssignments[id] || []).includes(tagId)) onToggleTag(id, tagId)
      })
    } else {
      // Add to those that don't have it
      itemIds.forEach(id => {
        if (!(tagAssignments[id] || []).includes(tagId)) onToggleTag(id, tagId)
      })
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="xs" className="gap-2">
          <Tag className="h-3.5 w-3.5" /> Tags ({itemIds.length})
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="center"
        sideOffset={12}
        className="w-72 p-0 overflow-hidden rounded-lg shadow-md border-border"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-3 pt-3 pb-2">
          <div className="flex items-center gap-2 mb-2">
            <Tag className="h-3.5 w-3.5 text-primary" />
            <span className="text-xs font-semibold">Tag {itemIds.length} item{itemIds.length !== 1 ? "s" : ""}</span>
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
            <Input
              ref={inputRef}
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('knowledge.search_tags')}
              className="h-8 border-0 bg-muted/50 pl-7 text-xs"
            />
          </div>
        </div>

        {/* Tag List */}
        <div className="max-h-52 overflow-y-auto px-1.5 pb-1.5">
          {filtered.length === 0 ? (
            <div className="py-6 text-center">
              <p className="text-xs text-muted-foreground">No tags found</p>
            </div>
          ) : (
            <div className="space-y-1">
              {filtered.map(tag => {
                const count = tagCounts[tag.id] || 0
                const allHave = count === itemIds.length
                const someHave = count > 0 && !allHave
                return (
                  <Button
                    key={tag.id}
                    variant="ghost"
                    size="xs"
                    onClick={(e) => { e.stopPropagation(); handleToggle(tag.id) }}
                    className={cn(
                      "w-full justify-start rounded-lg text-left",
                      allHave && "bg-primary/10"
                    )}
                  >
                    <span
                      className="h-3 w-3 rounded-full shrink-0 ring-1 ring-black/5 transition-transform duration-150"
                      style={{ backgroundColor: tag.color, transform: allHave ? "scale(1.15)" : "scale(1)" }}
                    />
                    <span className={cn("flex-1 text-xs truncate transition-colors", allHave ? "font-medium text-foreground" : "text-muted-foreground")}>
                      {tag.name}
                    </span>
                    {someHave && (
                      <span className="text-[9px] text-muted-foreground shrink-0">{count}/{itemIds.length}</span>
                    )}
                    <div className={cn(
                      "flex items-center justify-center h-4 w-4 rounded-md shrink-0 transition-all duration-200",
                      allHave ? "bg-primary text-primary-foreground scale-100"
                        : someHave ? "bg-primary/40 text-primary-foreground scale-100"
                        : "bg-muted/60 scale-90 opacity-0"
                    )}>
                      {allHave ? <Check className="h-2.5 w-2.5" /> : someHave ? <span className="block h-1.5 w-1.5 rounded-md bg-primary-foreground" /> : <Check className="h-2.5 w-2.5" />}
                    </div>
                  </Button>
                )
              })}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

/* ------------------------------------------------------------------ */

interface TagProps {
  tagAssignments: Record<string, string[]>
  onToggleTag: (itemId: string, tagId: string) => void
  onCreateTag?: (itemId: string, name: string) => void | Promise<void> | Promise<void>
  getItemTags: (itemId: string) => AvailableTag[]
  availableTags: AvailableTag[]
}

function GridView({ items, allItems, onOpenFolder, onEdit, onEditFolder, onDelete, onDownload, selectedIds, onToggleSelect, tagAssignments, onToggleTag, getItemTags, availableTags, ...drag }: {
  items: ContentItem[]; allItems: ContentItem[]; onOpenFolder: (id: string) => void
  onEdit: (item: ContentItem) => void; onEditFolder: (item: ContentItem) => void; onDelete: (id: string) => void
  onDownload: (item: ContentItem) => void
} & DragProps & SelectProps & TagProps) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
      {items.map((item) => (
        <GridCard key={item.id} item={item} allItems={allItems} onOpenFolder={onOpenFolder} onEdit={onEdit} onEditFolder={onEditFolder} onDelete={onDelete} onDownload={onDownload}
          isSelected={selectedIds.has(item.id)} onToggleSelect={onToggleSelect}
          tagAssignments={tagAssignments} onToggleTag={onToggleTag} getItemTags={getItemTags} availableTags={availableTags}
          {...drag} />
      ))}
    </div>
  )
}

function GridCard({ item, allItems, onOpenFolder, onEdit, onEditFolder, onDelete, onDownload, draggingIds, dropTargetId, onDragStart, onDragEnd, onDragOver, onDragLeave, onDrop, isSelected, onToggleSelect, tagAssignments, onToggleTag, onCreateTag, getItemTags, availableTags }: {
  item: ContentItem; allItems: ContentItem[]; onOpenFolder: (id: string) => void
  onEdit: (item: ContentItem) => void; onEditFolder: (item: ContentItem) => void; onDelete: (id: string) => void
  onDownload: (item: ContentItem) => void
  isSelected?: boolean; onToggleSelect?: (id: string, multi: boolean) => void
} & DragProps & TagProps) {
  const { t } = useTranslation()
  const isFolder = item.kind === "folder"
  const isDragging = draggingIds.has(item.id)
  const isDropTarget = isFolder && dropTargetId === item.id && !draggingIds.has(item.id)

  // For folders: get ALL direct children for masonry preview
  const folderChildren = React.useMemo(() => {
    if (!isFolder) return []
    return allItems.filter((i) => i.parentId === item.id)
  }, [isFolder, allItems, item.id])
  const totalChildren = folderChildren.length

  return (
    <Card
      data-content-card={item.id}
      draggable
      onDragStart={(e) => onDragStart(e, item.id)}
      onDragEnd={onDragEnd}
      {...(isFolder ? {
        onDragOver: (e: React.DragEvent) => onDragOver(e, item.id),
        onDragLeave: onDragLeave,
        onDrop: (e: React.DragEvent) => onDrop(e, item.id),
      } : {})}
      className={cn(
        "group relative overflow-hidden transition-all hover:ring-1 hover:ring-primary/30",
        isFolder && "cursor-pointer hover:bg-muted/40",
        isDragging && "opacity-40 scale-[0.97] ring-1 ring-primary/20",
        isDropTarget && "ring-2 ring-primary bg-primary/5 scale-[1.02] shadow-md",
        isSelected && "ring-2 ring-primary bg-primary/5",
      )}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest("button")) return
        if (e.metaKey || e.ctrlKey || e.shiftKey) {
          e.preventDefault()
          onToggleSelect?.(item.id, true)
        } else if (isFolder) {
          onOpenFolder(item.id)
        }
      }}
    >
      {/* hover action icons — order: Download, Tags, Edit, Delete */}
      <div className="absolute right-1.5 top-1.5 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10" onClick={(e) => e.stopPropagation()}>
        <TooltipProvider delayDuration={300}>
          {/* Download — only for non-folders */}
          {item.kind === "document" && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-xs"
                  className="rounded-lg bg-background shadow-sm border hover:bg-primary/10 hover:border-primary/30 hover:shadow-md text-muted-foreground hover:text-primary"
                  onClick={() => onDownload(item)}
                  title="Download"
                  aria-label="Download document"
                >
                  <Download className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">Download</TooltipContent>
            </Tooltip>
          )}
          {/* Tags — only for non-folders */}
          {!isFolder && (
            <TagSelectorPopover
              onCreateTag={onCreateTag}
              itemId={item.id}
              assignedTagIds={tagAssignments[item.id] || []}
              onToggleTag={onToggleTag}
              availableTags={availableTags}
            >
              <Button variant="ghost" size="icon-xs" className={cn(
                "rounded-lg shadow-sm border hover:shadow-md",
                (tagAssignments[item.id] || []).length > 0
                  ? "text-foreground"
                  : "text-muted-foreground"
              )}>
                <Tag className="h-3.5 w-3.5" />
              </Button>
            </TagSelectorPopover>
          )}
          {/* Edit / Rename */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-xs"
                className="rounded-lg bg-background shadow-sm border hover:bg-background hover:shadow-md text-muted-foreground hover:text-foreground"
                onClick={() => isFolder ? onEditFolder(item) : onEdit(item)}
                aria-label={isFolder ? "Edit folder" : "Edit item"}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">{isFolder ? t("knowledge.content.editFolder") : "Edit"}</TooltipContent>
          </Tooltip>
          {/* Delete */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon-xs" className="text-destructive hover:text-destructive" onClick={() => onDelete(item.id)}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">{t("knowledge.content.delete")}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <CardContent className="p-0 flex flex-col">
        {/* thumbnail area */}
        {isFolder ? (
          <FolderPreview items={folderChildren} />
        ) : item.kind === "url" ? (
          <div className="flex items-center justify-center h-28 bg-primary/5">
            <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10">
              <Globe className="h-5 w-5 text-primary/80" />
            </div>
          </div>
        ) : (
          <DocThumbnail item={item} className="h-28" />
        )}

        {/* info bar */}
        <div className="px-3 py-2.5 space-y-1">
          <div className="flex items-center gap-2 min-w-0">
            {isFolder && <Folder className="h-3.5 w-3.5 shrink-0 text-warning" />}
            <span className="text-sm font-medium truncate">{item.name}</span>
          </div>
          {isFolder ? (
            <div className="text-[11px] text-muted-foreground">
              {totalChildren} {totalChildren === 1 ? t("knowledge.content.file") : t("knowledge.content.items")}
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-muted-foreground truncate">
                  {item.kind === "url" ? item.url : item.size}
                </span>
                {item.status && (
                  <Badge variant="outline" className={cn("text-[9px] px-1 py-0 shrink-0", statusColor(item.status))}>
                    {t(`knowledge.content.status.${item.status}`)}
                  </Badge>
                )}
              </div>
              {/* Assigned tags — single line, counter always visible */}
              {(() => {
                const itemTags = getItemTags(item.id)
                if (itemTags.length === 0) return null
                return (
                  <div className="flex items-center gap-1 pt-0.5 min-w-0">
                    <div className="flex items-center gap-1 overflow-hidden min-w-0 flex-1">
                      {itemTags.slice(0, 3).map(tag => (
                        <span key={tag.id} className="shrink-0"><TagPill name={tag.name} color={tag.color} /></span>
                      ))}
                    </div>
                    {itemTags.length > 3 && (
                      <span className="text-[9px] text-muted-foreground shrink-0">+{itemTags.length - 3}</span>
                    )}
                  </div>
                )
              })()}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

/** Dynamic masonry-like preview of folder contents */
function FolderPreview({ items }: { items: ContentItem[] }) {
  const count = items.length
  const MAX_VISIBLE = 6
  const visible = items.slice(0, MAX_VISIBLE)
  const extra = Math.max(0, count - MAX_VISIBLE)

  if (count === 0) {
    // Empty folder — show subtle placeholder
    return (
      <div className="flex items-center justify-center h-24 bg-muted/10 rounded-t-lg">
        <Folder className="h-6 w-6 text-muted-foreground" />
      </div>
    )
  }

  // Compute layout class based on visible count
  // 1 item  → full width single tile
  // 2 items → 2 columns equal
  // 3 items → first takes full width, two below
  // 4 items → 2×2 grid
  // 5 items → first row 3, second row 2
  // 6 items → 3×2 grid
  const layoutClass =
    visible.length === 1 ? "grid-cols-1" :
    visible.length === 2 ? "grid-cols-2" :
    visible.length === 3 ? "grid-cols-2" :
    visible.length === 4 ? "grid-cols-2" :
    "grid-cols-3"

  // For 3 items, the first child spans both columns
  const spanFirst = visible.length === 3

  return (
    <div className={cn("grid gap-1 h-24 bg-muted/20 overflow-hidden rounded-t-lg", layoutClass)}>
      {visible.map((child, i) => {
        const isLast = i === visible.length - 1 && extra > 0
        return (
          <div
            key={child.id}
            className={cn(
              "relative min-h-0 overflow-hidden",
              spanFirst && i === 0 && "col-span-2",
            )}
          >
            {/* mini preview per item type */}
            {child.kind === "folder" ? (
              <div className={cn("absolute inset-0 flex items-center justify-center", getTileBg(child))}>
                <Folder className="h-4 w-4 text-warning/70" />
              </div>
            ) : child.kind === "url" ? (
              <div className={cn("absolute inset-0 flex items-center justify-center", getTileBg(child))}>
                <Globe className="h-3.5 w-3.5 text-primary/70" />
              </div>
            ) : (
              <DocThumbnail item={child} className="absolute inset-0" />
            )}
            {/* file type label */}
            <span className="absolute bottom-0.5 left-1 text-[8px] font-medium text-muted-foreground uppercase leading-none z-[1]">
              {child.kind === "url" ? "url" : child.kind === "folder" ? "" : child.fileType || ""}
            </span>
            {/* +N overlay on last visible tile */}
            {isLast && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-[2]">
                <span className="text-sm font-semibold text-white">+{extra}</span>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

/* ================================================================== */
/*  List view                                                          */
/* ================================================================== */

function ListView({ items, onOpenFolder, onEdit, onEditFolder, onDelete, onDownload, sortField, sortDir, onSort, draggingIds, dropTargetId, onDragStart, onDragEnd, onDragOver, onDragLeave, onDrop, selectedIds, onToggleSelect, tagAssignments, onToggleTag, onCreateTag, getItemTags, availableTags }: {
  items: ContentItem[]; onOpenFolder: (id: string) => void
  onEdit: (item: ContentItem) => void; onEditFolder: (item: ContentItem) => void; onDelete: (id: string) => void
  onDownload: (item: ContentItem) => void
  sortField: SortField; sortDir: SortDir; onSort: (field: SortField) => void
} & DragProps & SelectProps & TagProps) {
  const { t } = useTranslation()

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-0 group-hover/th:opacity-40 transition-opacity" />
    return sortDir === "asc"
      ? <ArrowUp className="h-3 w-3 ml-1 text-primary" />
      : <ArrowDown className="h-3 w-3 ml-1 text-primary" />
  }

  const ThButton = ({ field, label, className: cls }: { field: SortField; label: string; className?: string }) => (
    <th className={cn("py-2.5 px-4", cls)}>
      <Button
        variant="ghost"
        size="xs"
        onClick={() => onSort(field)}
        className="group/th -mx-2 gap-1 font-medium select-none"
      >
        {label}<SortIcon field={field} />
      </Button>
    </th>
  )

  return (
    <div className="rounded-lg border overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/30">
            <ThButton field="name" label={t("knowledge.content.colName")} className="text-left" />
            <ThButton field="type" label={t("knowledge.content.colType")} className="text-left hidden md:table-cell" />
            <ThButton field="size" label={t("knowledge.content.colSize")} className="text-left hidden lg:table-cell" />
            <ThButton field="date" label={t("knowledge.content.colDate")} className="text-left hidden lg:table-cell" />
            <ThButton field="status" label={t("knowledge.content.colStatus")} className="text-left hidden sm:table-cell" />
            <th className="py-2.5 px-4 w-20"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const isFolder = item.kind === "folder"
            const isDragging = draggingIds.has(item.id)
            const isDropTarget = isFolder && dropTargetId === item.id && !draggingIds.has(item.id)
            const isSelected = selectedIds.has(item.id)
            return (
              <tr
                key={item.id}
                data-content-card={item.id}
                draggable
                onDragStart={(e) => onDragStart(e, item.id)}
                onDragEnd={onDragEnd}
                {...(isFolder ? {
                  onDragOver: (e: React.DragEvent<HTMLTableRowElement>) => onDragOver(e, item.id),
                  onDragLeave: onDragLeave,
                  onDrop: (e: React.DragEvent<HTMLTableRowElement>) => onDrop(e, item.id),
                } : {})}
                className={cn(
                  "border-b last:border-b-0 transition-all group",
                  isFolder ? "cursor-pointer hover:bg-muted/40" : "hover:bg-muted/20",
                  isDragging && "opacity-40",
                  isDropTarget && "bg-primary/5 ring-1 ring-inset ring-primary shadow-[inset_0_0_12px_0_rgba(var(--primary)/0.08)]",
                  isSelected && "bg-primary/5",
                )}
                onClick={(e) => {
                  if ((e.target as HTMLElement).closest("button")) return
                  if (e.metaKey || e.ctrlKey || e.shiftKey) {
                    e.preventDefault()
                    onToggleSelect(item.id, true)
                  } else if (isFolder) {
                    onOpenFolder(item.id)
                  }
                }}
              >
                <td className="py-2.5 px-4">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className={cn("flex items-center justify-center w-8 h-8 rounded-md shrink-0", isFolder ? "bg-warning/10" : "bg-muted/60")}>
                      {isFolder ? <FolderOpen className="h-4 w-4 text-warning" /> : getFileIcon(item)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 min-w-0">
                        {isFolder && <Folder className="h-3.5 w-3.5 shrink-0 text-warning" />}
                        <span className="font-medium truncate">{item.name}</span>
                        {/* Inline tag pills for list view */}
                        {!isFolder && (() => {
                          const itemTags = getItemTags(item.id)
                          if (itemTags.length === 0) return null
                          return (
                            <span className="ml-1.5 hidden shrink-0 items-center gap-1 sm:inline-flex">
                              {itemTags.slice(0, 2).map(tag => (
                                <TagPill key={tag.id} name={tag.name} color={tag.color} />
                              ))}
                              {itemTags.length > 2 && (
                                <span className="text-[9px] text-muted-foreground">+{itemTags.length - 2}</span>
                              )}
                            </span>
                          )
                        })()}
                      </div>
                      {item.description && <div className="text-xs text-muted-foreground truncate">{item.description}</div>}
                    </div>
                  </div>
                </td>
                <td className="py-2.5 px-4 hidden md:table-cell text-muted-foreground">
                  {isFolder ? t("knowledge.content.folderType") : item.kind === "url" ? "URL" : (item.fileType || "—").toUpperCase()}
                </td>
                <td className="py-2.5 px-4 hidden lg:table-cell text-muted-foreground">{!isFolder ? (item.kind === "url" ? "—" : item.size || "—") : "—"}</td>
                <td className="py-2.5 px-4 hidden lg:table-cell text-muted-foreground">{item.addedDate ? new Date(item.addedDate).toLocaleDateString() : "—"}</td>
                <td className="py-2.5 px-4 hidden sm:table-cell">
                  {!isFolder && item.status && (
                    <Badge variant="outline" className={cn("text-[10px] px-1.5 py-0", statusColor(item.status))}>
                      {t(`knowledge.content.status.${item.status}`)}
                    </Badge>
                  )}
                </td>
                <td className="py-2.5 px-4 text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <TooltipProvider delayDuration={300}>
                      {!isFolder && item.kind === "document" && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon-xs"
                              className="hover:bg-primary/10 hover:text-primary"
                              onClick={() => onDownload(item)}
                              title="Download"
                              aria-label="Download document"
                            >
                              <Download className="h-3.5 w-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="bottom" className="text-xs">Download</TooltipContent>
                        </Tooltip>
                      )}
                      {/* Tags — only for non-folders */}
                      {!isFolder && (
                        <TagSelectorPopover
                          onCreateTag={onCreateTag}
                          itemId={item.id}
                          assignedTagIds={tagAssignments[item.id] || []}
                          onToggleTag={onToggleTag}
                          availableTags={availableTags}
                        >
                          <Button variant="ghost" size="icon-xs" className={cn((tagAssignments[item.id] || []).length > 0 ? "text-foreground" : "text-muted-foreground")}>
                            <Tag className="h-3.5 w-3.5" />
                          </Button>
                        </TagSelectorPopover>
                      )}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon-xs"
                            className="hover:bg-muted"
                            onClick={() => isFolder ? onEditFolder(item) : onEdit(item)}
                            aria-label={isFolder ? "Edit folder" : "Edit item"}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="text-xs">{isFolder ? t("knowledge.content.editFolder") : t("knowledge.content.rename")}</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="icon-xs" className="hover:bg-destructive/10 hover:text-destructive" onClick={() => onDelete(item.id)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="text-xs">{t("knowledge.content.delete")}</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
